import React, { useEffect, useRef, useState, useCallback, memo, forwardRef } from 'react';
import styled from 'styled-components';
import FallbackStarMap from './FallbackStarMap';
import { jsPDF } from 'jspdf';
import { svg2pdf } from 'svg2pdf.js';

// Note: d3-celestial is now loaded directly in public/index.html

const StarMapContainer = styled.div.attrs({ className: 'starmap-container' })`
  width: 100%;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #000;
  border-radius: 8px;
  overflow: visible;
  max-width: 100%;
  padding: 0;
  box-sizing: border-box;
  margin: 0 auto;
`;

const MapFrame = styled.div.attrs({ className: 'starmap-frame' })`
  width: 100%;
  height: 0;
  padding-bottom: 100%; /* Using 100% for a perfect square aspect ratio */
  position: relative;
  border: none;
  background-color: transparent;
  box-sizing: border-box;
  overflow: hidden;
  border-radius: 50%; /* Using 50% for a circular map */
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.5);
`;

const MapCanvas = styled.div.attrs({ className: 'starmap-canvas', id: 'celestial-map-container' })`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 50%; /* Using 50% for a circular map */
  display: flex;
  justify-content: center;
  align-items: center;
  
  /* Ensure the d3-celestial canvas is properly sized */
  & canvas {
    width: 100% !important;
    height: 100% !important;
    border-radius: 50%; /* Using 50% for a circular map */
    overflow: hidden;
    object-fit: contain;
    display: block; /* Ensure no extra space */
    margin: 0 auto; /* Center canvas */
    position: absolute;
    top: 0;
    left: 0;
  }
  
  /* Hide zoom controls */
  & #celestial-zoomin,
  & #celestial-zoomout {
    display: none !important;
  }
`;

// Styled components removed - they were unused

const ErrorMessage = styled.div.attrs({ className: 'starmap-error-message' })`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 8px;
  background-color: rgba(0, 0, 0, 0.7);
  color: #ccc;
  font-size: 12px;
  text-align: center;
  opacity: 0.8;
  transition: opacity 0.3s ease;
`;

// Helper function to check if celestial is fully loaded
const isCelestialReady = () => {
    return window.Celestial && window.Celestial.display;
};

// Format date for display
const formatDate = (dateString) => {
    if (!dateString) return '';

    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
        });
    } catch (e) {
        return '';
    }
};

// Format location for display
const formatLocation = (location) => {
    if (!location) return '';

    try {
        let lat, lon;
        if (typeof location === 'string') {
            // Handle string format like "lat,lon"
            [lat, lon] = location.split(',').map(n => parseFloat(n.trim()));
        } else if (location.latitude !== undefined && location.longitude !== undefined) {
            // Handle object format {latitude, longitude}
            lat = parseFloat(location.latitude);
            lon = parseFloat(location.longitude);
        } else {
            return '';
        }

        return `${Math.abs(lat).toFixed(2)}° ${lat >= 0 ? 'N' : 'S'}, ${Math.abs(lon).toFixed(2)}° ${lon >= 0 ? 'E' : 'W'}`;
    } catch (e) {
        return '';
    }
};

const StarMapGenerator = forwardRef(({
    date,
    location,
    size,
    caption,
    mapStyle,
    showStars = true,
    showConstellations = true,
    showConstellationNames = true,
    showGrid = true,
    showDeepSkyObjects = true,
    showEcliptic = true,
    onError,
    onLoaded,
    regenerate = true
}, ref) => {
    // console.log('StarMapGenerator (re)render - location:', location, 'regenerate:', regenerate);

    const mapContainerRef = useRef(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [celestialAttempts, setCelestialAttempts] = useState(0);
    // Define refs at component level, not inside useEffect
    const lastDateRef = useRef(date);
    const lastLocationRef = useRef(location);
    const mountRef = useRef(false);
    // Add debounce ref for smooth updates
    const updateTimeoutRef = useRef(null);
    // Add initialization state to prevent duplicate loading
    const isInitializingRef = useRef(false);
    const hasInitializedRef = useRef(false);

    // Ensure map container exists early
    useEffect(() => {
        // Create map-container early if it doesn't exist
        if (mapContainerRef.current && !document.getElementById('map-container')) {
            console.log('Creating map container element early');
            const mapElement = document.createElement('div');
            mapElement.id = 'map-container';
            mapElement.style.width = '100%';
            mapElement.style.height = '100%';
            mapElement.style.position = 'absolute';
            mapElement.style.top = '0';
            mapElement.style.left = '0';
            mapElement.style.display = 'block'; // Changed from flex to block
            mapElement.style.zIndex = '50';
            mapElement.style.visibility = 'visible';
            mapElement.style.opacity = '1';
            mapElement.style.overflow = 'hidden';

            // Clear any existing content first
            mapContainerRef.current.innerHTML = '';
            mapContainerRef.current.appendChild(mapElement);

            console.log('Map container created and added to DOM:', {
                id: mapElement.id,
                parent: mapElement.parentElement?.className,
                dimensions: `${mapElement.style.width} x ${mapElement.style.height}`
            });
        }
    }, []);

    // Progressive rendering helper function
    const enableProgressiveRendering = useCallback(() => {
        // Monitor memory during celestial data loading
        const monitorMemoryDuringLoad = () => {
            if (performance.memory) {
                const memory = {
                    used: Math.round(performance.memory.usedJSHeapSize / 1024 / 1024),
                    total: Math.round(performance.memory.totalJSHeapSize / 1024 / 1024),
                    limit: Math.round(performance.memory.jsHeapSizeLimit / 1024 / 1024)
                };

                console.log(`Memory during celestial load: ${memory.used}MB used, ${memory.total}MB total`);

                // If memory pressure is high during loading, switch to minimal config
                const memoryPressure = memory.used / memory.limit;
                if (memoryPressure > 0.7) { // Only switch to minimal at very high pressure
                    console.warn(`High memory pressure during loading (${(memoryPressure * 100).toFixed(1)}%), switching to minimal rendering`);

                    // Return minimal configuration that still looks good
                    return {
                        stars: { show: showStars, limit: 4, colors: true, designation: false, propername: false },
                        dsos: { show: showDeepSkyObjects, limit: 4 }, // Keep some DSOs
                        constellations: { show: showConstellations, names: showConstellationNames, lines: showConstellations, bounds: false },
                        mw: { show: false }, // Disable Milky Way only in minimal mode
                        performance: { antialias: false, alpha: false, preserveDrawingBuffer: false },
                        advanced: { adaptable: false, clip: true, buffer: 25 }
                    };
                }
            }
            return null; // Use normal config
        };

        return monitorMemoryDuringLoad;
    }, [showStars, showConstellations, showDeepSkyObjects]);

    // Enhanced memory cleanup function
    const performMemoryCleanup = useCallback(() => {
        // Clear any cached celestial data
        if (window.Celestial && window.Celestial.clear) {
            try {
                window.Celestial.clear();
            } catch (e) {
                console.log('Celestial clear not available or failed:', e.message);
            }
        }

        // Clear any canvas contexts that might be lingering
        const canvases = document.querySelectorAll('canvas');
        canvases.forEach(canvas => {
            try {
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    ctx.clearRect(0, 0, canvas.width, canvas.height);
                }
                // Force canvas deallocation
                canvas.width = 1;
                canvas.height = 1;
            } catch (e) {
                console.log('Canvas cleanup failed:', e.message);
            }
        });

        // Clear any image elements that might be cached
        const images = document.querySelectorAll('img');
        images.forEach(img => {
            if (img.src && img.src.startsWith('data:')) {
                img.src = '';
            }
        });

        // Force garbage collection if available
        if (window.gc) {
            console.log('Performing garbage collection');
            window.gc();
        }
    }, []);

    // Notify parent when map is successfully generated - moved before generateStarMapInternal to fix initialization error
    const notifyMapLoaded = useCallback((timeoutRef = null) => {
        console.log('📞 DEBUG: notifyMapLoaded called', {
            onLoadedExists: typeof onLoaded === 'function',
            date,
            location,
            hasInitialized: hasInitializedRef.current,
            isInitializing: isInitializingRef.current,
            hasTimeoutRef: !!timeoutRef
        });

        // Clear safety timeout if provided
        if (timeoutRef && timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            console.log('🔒 Cleared safety timeout');
        }

        if (typeof onLoaded === 'function') {
            console.log('Star map loaded - notifying parent');

            // Always notify parent immediately that the map is loaded
            onLoaded();

            // Update the refs to track the current values
            lastDateRef.current = date;
            lastLocationRef.current = location;

            // Reset regenerate flag to prevent infinite loops
            console.log('🔄 Resetting regenerate flag after successful load');

            // Mark initialization as complete
            isInitializingRef.current = false;
            hasInitializedRef.current = true;
            console.log('✅ Star map initialization completed successfully');

            // Fire the animation complete event after a short delay to hide loading indicator
            setTimeout(() => {
                console.log('Firing starmap-animation-complete event');
                window.dispatchEvent(new CustomEvent('starmap-animation-complete'));
            }, 500); // Short delay to ensure smooth transition
        }
    }, [onLoaded, date, location]);

    // Define generateStarMapInternal after notifyMapLoaded to avoid initialization errors
    const generateStarMapInternal = useCallback((forcedDate = null, forcedLocation = null) => {
        // Use forced values if provided, otherwise use props - this prevents stale closure issues
        const actualDate = forcedDate || date;
        const actualLocation = forcedLocation || location;

        console.log('⭐ DEBUG: generateStarMapInternal called with:', {
            date: actualDate,
            location: actualLocation,
            regenerate,
            originalDate: date,
            originalLocation: location,
            forcedDate,
            forcedLocation,
            dateType: typeof actualDate,
            locationType: typeof actualLocation,
            locationIsEmpty: actualLocation === '',
            locationIsNull: actualLocation === null,
            locationIsUndefined: actualLocation === undefined
        });

        // Early validation to prevent silent failures
        if (!actualDate || !actualLocation) {
            const error = `Missing required data: date=${actualDate}, location=${actualLocation}`;
            console.error('❌ CRITICAL:', error);
            setError(error);
            notifyMapLoaded();
            return;
        }

        // Check if Celestial is available with enhanced debugging
        console.log('🔍 CHECKING CELESTIAL AVAILABILITY:', {
            windowCelestial: !!window.Celestial,
            celestialDisplay: !!(window.Celestial && window.Celestial.display),
            celestialDisplayType: window.Celestial ? typeof window.Celestial.display : 'undefined',
            d3Available: !!window.d3,
            documentReady: document.readyState
        });

        if (!window.Celestial || typeof window.Celestial.display !== 'function') {
            console.log('❌ Celestial library not ready yet - waiting instead of using fallback');

            // Instead of immediately showing fallback, wait for the library
            if (celestialAttempts < 20) { // Increased attempt limit
                console.log('Incrementing celestial attempts to wait for library');
                setCelestialAttempts(prev => prev + 1);

                // Check again in a moment
                setTimeout(() => {
                    if (window.Celestial && typeof window.Celestial.display === 'function') {
                        console.log('Celestial library is now ready - generating high-quality map');
                        generateStarMapInternal(actualDate, actualLocation);
                    }
                }, 300 + celestialAttempts * 100); // Progressive delay

                return; // Don't show error, just wait
            }

            // Emergency fallback - check if the script needs to be loaded
            if (!document.querySelector('script[src*="d3-celestial.min.js"]')) {
                console.log('Attempting emergency load of d3-celestial library');

                // Create script tag for d3-celestial
                const script = document.createElement('script');
                script.src = `${window.location.origin}/d3-celestial.min.js`;
                script.async = true;
                script.onload = () => {
                    console.log('D3-celestial loaded via emergency fallback');
                    // Try again after script loads
                    setTimeout(() => {
                        if (window.Celestial && typeof window.Celestial.display === 'function') {
                            console.log('Celestial now available - retrying map generation');
                            generateStarMapInternal(actualDate, actualLocation);
                        }
                    }, 500);
                };
                document.head.appendChild(script);

                return; // Don't show error yet, wait for script to load
            }

            // Only show fallback after all attempts have been exhausted
            console.log('All attempts to load Celestial library have been exhausted');
            setError('Loading high-quality star map. Please wait...');
            return;
        }

        // Enhanced data file validation
        console.log('🔍 VALIDATING CELESTIAL DATA FILES:');
        const dataPath = `${window.location.origin}/d3-celestial-data/`;
        const requiredFiles = ['stars.6.json', 'constellations.json', 'constellations.lines.json'];

        // Check if we can access the data files
        const checkDataFiles = async () => {
            const fileChecks = await Promise.all(
                requiredFiles.map(async (file) => {
                    try {
                        const response = await fetch(`${dataPath}${file}`, { method: 'HEAD' });
                        console.log(`📂 ${file}: ${response.ok ? '✅ Available' : '❌ Missing'} (status: ${response.status})`);
                        return { file, available: response.ok, status: response.status };
                    } catch (error) {
                        console.log(`📂 ${file}: ❌ Error checking (${error.message})`);
                        return { file, available: false, error: error.message };
                    }
                })
            );

            const missingFiles = fileChecks.filter(check => !check.available);
            if (missingFiles.length > 0) {
                const errorMsg = `Missing celestial data files: ${missingFiles.map(f => f.file).join(', ')}`;
                console.error('❌ DATA FILES:', errorMsg);
                setError(`Data loading error: ${errorMsg}`);
                notifyMapLoaded();
                return false;
            }

            console.log('✅ All required celestial data files are available');
            return true;
        };

        try {
            // Parse the date - create a new Date object to ensure proper date handling
            const dateObj = new Date(actualDate);

            // Add forceful logging of date to debug date parsing issues
            console.log('📅 CRITICAL DATE DEBUG:', {
                rawDate: actualDate,
                parsedDate: dateObj,
                isoString: dateObj.toISOString(),
                timestamp: dateObj.getTime(),
                validDate: !isNaN(dateObj.getTime()),
                dateYear: dateObj.getFullYear(),
                dateMonth: dateObj.getMonth() + 1,
                dateDay: dateObj.getDate()
            });

            // Extract location coordinates, handling both string and object formats
            let latitude, longitude;

            console.log('🌍 DEBUG: Starting location parsing', {
                location: actualLocation,
                locationType: typeof actualLocation,
                locationStringValue: String(actualLocation),
                locationLength: actualLocation ? actualLocation.length : 0
            });

            if (typeof actualLocation === 'string') {
                // Handle string format like "lat,lon"
                const coords = actualLocation.split(',');
                latitude = parseFloat(coords[0]);
                longitude = parseFloat(coords[1]);
                console.log('🌍 DEBUG - Parsed string location:', {
                    original: actualLocation,
                    coords,
                    latitude,
                    longitude,
                    latitudeIsNaN: isNaN(latitude),
                    longitudeIsNaN: isNaN(longitude)
                });
            } else {
                // Handle object format {latitude, longitude}
                latitude = parseFloat(actualLocation.latitude);
                longitude = parseFloat(actualLocation.longitude);
                console.log('🌍 DEBUG - Parsed object location:', {
                    original: actualLocation,
                    latitude,
                    longitude,
                    latitudeIsNaN: isNaN(latitude),
                    longitudeIsNaN: isNaN(longitude)
                });
            }

            // Validate coordinates
            if (isNaN(latitude) || isNaN(longitude)) {
                console.error('❌ DEBUG: Invalid location coordinates detected:', {
                    location: actualLocation,
                    latitude,
                    longitude,
                    latitudeIsNaN: isNaN(latitude),
                    longitudeIsNaN: isNaN(longitude)
                });
                setError('Invalid location format. Using simplified star map instead.');

                // Reset initialization flags when there's an error
                isInitializingRef.current = false;
                hasInitializedRef.current = false;

                // Notify parent that loading is complete even though map generation failed
                notifyMapLoaded();
                return;
            }

            // Enhanced coordinate validation
            if (latitude < -90 || latitude > 90) {
                const error = `Invalid latitude: ${latitude} (must be -90 to 90)`;
                console.error('❌ COORDINATES:', error);
                setError(error);
                notifyMapLoaded();
                return;
            }

            if (longitude < -180 || longitude > 180) {
                const error = `Invalid longitude: ${longitude} (must be -180 to 180)`;
                console.error('❌ COORDINATES:', error);
                setError(error);
                notifyMapLoaded();
                return;
            }

            // CRITICAL DEBUG INFO
            console.log('🎯 STARMAP GENERATION PARAMETERS:', {
                location_raw: actualLocation,
                parsed_latitude: latitude,
                parsed_longitude: longitude,
                date: dateObj,
                dateValid: !isNaN(dateObj.getTime()),
                coordinatesValid: !isNaN(latitude) && !isNaN(longitude)
            });

            // Check data files before proceeding
            checkDataFiles().then(dataFilesOk => {
                if (!dataFilesOk) return; // Error already logged and handled

                // Continue with map generation...
                proceedWithMapGeneration(dateObj, latitude, longitude);
            });

        } catch (err) {
            console.error('❌ CRITICAL ERROR in generateStarMapInternal:', err);
            setError(`Critical error: ${err.message}`);
            notifyMapLoaded();
        }

        // Move the main generation logic to a separate function for better error handling
        const proceedWithMapGeneration = async (dateObj, latitude, longitude) => {
            try {
                // Get container dimensions or use default
                const containerWidth = mapContainerRef.current?.clientWidth || size || 600;

                // Create configuration with enhanced validation
                console.log('🎯 CREATING CELESTIAL CONFIGURATION');

                // Fix date - set time to noon instead of midnight for better celestial calculations
                const properDate = new Date(dateObj);
                properDate.setHours(12, 0, 0, 0); // Set to noon local time

                console.log('📅 ADJUSTED DATE FOR CELESTIAL:', {
                    originalDate: dateObj.toISOString(),
                    adjustedDate: properDate.toISOString(),
                    localTime: properDate.toLocaleString()
                });

                const config = {
                    width: containerWidth || 600,
                    height: containerWidth || 600,
                    container: "map-container",
                    datapath: `${window.location.origin}/d3-celestial-data/`,
                    projection: "stereographic",
                    background: {
                        fill: "transparent",
                        stroke: "transparent",
                        opacity: 0,
                        width: 0
                    },
                    date: properDate, // Use adjusted date
                    center: [0, 0, 0], // Ensure center has 3 elements
                    follow: "zenith", // Follow the zenith of the observer
                    geopos: [latitude, longitude], // Set the geographical position directly
                    transform: "equatorial", // Use equatorial mode for proper location-based view
                    transition: false, // Disable transitions to avoid render issues
                    adaptable: false, // Disable adaptable to avoid conflicts
                    animationDuration: 0, // No animations
                    interactive: false,
                    form: false,
                    controls: false,
                    zoomBy: 1,

                    // Stars configuration with validation
                    stars: {
                        show: showStars,
                        limit: 6,
                        colors: true,
                        style: {
                            fill: "#ffffff",
                            opacity: 0.8,
                            stroke: "none",
                            width: 0
                        },
                        designation: false,
                        propername: false,
                        namelimit: 3,
                        size: 7,
                        exponent: -0.28,
                        data: "stars.6.json"
                    },

                    // Constellations configuration
                    constellations: {
                        show: showConstellations,
                        names: showConstellationNames,
                        lines: showConstellations,
                        linestyle: {
                            stroke: "#cccccc",
                            width: 1,
                            opacity: 0.7
                        },
                        namestyle: {
                            fill: "#cccccc",
                            font: "11px sans-serif",
                            opacity: 0.8
                        },
                        bounds: false,
                        boundsstyle: {
                            stroke: "#cccccc",
                            width: 0.5,
                            opacity: 0.4,
                            dash: [2, 4]
                        }
                    },

                    // Deep sky objects
                    dsos: {
                        show: showDeepSkyObjects,
                        limit: 6,
                        colors: true,
                        style: {
                            fill: "#cccccc",
                            stroke: "#cccccc",
                            opacity: 0.7,
                            width: 2
                        },
                        names: false,
                        namelimit: 6,
                        size: null,
                        exponent: 1.4,
                        data: "dsos.bright.json"
                    },

                    // Grid lines and coordinate systems
                    lines: {
                        graticule: {
                            show: showGrid,
                            stroke: "#8989b3",
                            width: 1,
                            opacity: 0.6
                        },
                        equatorial: {
                            show: showGrid,
                            stroke: "#8989b3",
                            width: 1,
                            opacity: 0.6
                        },
                        ecliptic: {
                            show: showEcliptic,
                            stroke: "#dda74f",
                            width: 1.5,
                            opacity: 0.7
                        },
                        galactic: {
                            show: false
                        },
                        supergalactic: {
                            show: false
                        }
                    },

                    // Milky Way
                    mw: {
                        show: true,
                        style: {
                            fill: "#996633",
                            opacity: 0.15
                        }
                    }
                };

                console.log('🎯 FINAL CONFIGURATION:', {
                    container: config.container,
                    width: config.width,
                    height: config.height,
                    date: config.date.toISOString(),
                    geopos: config.geopos,
                    latitude: config.geopos[0],
                    longitude: config.geopos[1],
                    datapath: config.datapath,
                    showStars: config.stars.show,
                    showConstellations: config.constellations.show,
                    starsLimit: config.stars.limit,
                    projection: config.projection,
                    transform: config.transform,
                    follow: config.follow
                });

                // Log the complete configuration object for debugging
                console.log('🔧 COMPLETE CONFIG OBJECT:', JSON.stringify(config, (key, value) => {
                    if (key === 'date' && value instanceof Date) {
                        return value.toISOString();
                    }
                    return value;
                }, 2));

                // Ensure container exists before calling display
                const mapContainer = document.getElementById('map-container');
                if (!mapContainer) {
                    console.error('❌ Map container not found!');
                    setError('Map container not available');
                    notifyMapLoaded();
                    return;
                }

                console.log('✅ Map container found, proceeding with Celestial.display');

                // Clear any existing content and prepare container properly
                mapContainer.innerHTML = '';

                // Ensure container has proper CSS properties for d3-celestial
                mapContainer.style.position = 'relative';
                mapContainer.style.width = `${containerWidth}px`;
                mapContainer.style.height = `${containerWidth}px`;
                mapContainer.style.overflow = 'hidden';

                console.log('🎨 CONTAINER PREPARED:', {
                    id: mapContainer.id,
                    width: mapContainer.style.width,
                    height: mapContainer.style.height,
                    position: mapContainer.style.position,
                    childrenCount: mapContainer.children.length
                });

                // Call Celestial.display with comprehensive error handling and fallback
                const attemptDisplay = async (configToUse, attemptNumber = 1) => {
                    try {
                        console.log(`🚀 ATTEMPT ${attemptNumber}: Calling window.Celestial.display...`);

                        // Add a small delay to ensure DOM is fully ready
                        await new Promise(resolve => setTimeout(resolve, 100));

                        // Double-check container still exists
                        const currentContainer = document.getElementById('map-container');
                        if (!currentContainer) {
                            console.error(`❌ ATTEMPT ${attemptNumber}: Container disappeared!`);
                            return false;
                        }

                        // Reset d3-celestial state if possible
                        if (window.Celestial && typeof window.Celestial.clear === 'function') {
                            try {
                                window.Celestial.clear();
                                console.log(`🧹 ATTEMPT ${attemptNumber}: Cleared previous Celestial state`);
                            } catch (clearError) {
                                console.log(`⚠️ ATTEMPT ${attemptNumber}: Could not clear Celestial state:`, clearError.message);
                            }
                        }

                        // Also try to reset any global d3 selections that might conflict
                        if (window.d3) {
                            try {
                                // Remove any existing celestial-related elements
                                window.d3.selectAll('.celestial').remove();
                                window.d3.selectAll('#celestial-map').remove();
                                console.log(`🧹 ATTEMPT ${attemptNumber}: Cleared d3 selections`);
                            } catch (d3Error) {
                                console.log(`⚠️ ATTEMPT ${attemptNumber}: Could not clear d3 selections:`, d3Error.message);
                            }
                        }

                        // Wrap the d3-celestial call in a try-catch to handle internal errors
                        try {
                            console.log(`🎯 ATTEMPT ${attemptNumber}: About to call Celestial.display with container:`, {
                                container: configToUse.container,
                                containerExists: !!document.getElementById(configToUse.container),
                                containerParent: document.getElementById(configToUse.container)?.parentElement?.tagName,
                                celestialVersion: window.Celestial?.version || 'unknown',
                                configDate: configToUse.date,
                                configGeopos: configToUse.geopos,
                                configStars: configToUse.stars?.show
                            });

                            // Add additional delay for complex configurations
                            if (attemptNumber === 1) {
                                await new Promise(resolve => setTimeout(resolve, 300));
                            }

                            window.Celestial.display(configToUse);

                            // Log success of the display call
                            console.log(`✅ ATTEMPT ${attemptNumber}: Celestial.display call completed without error`);
                        } catch (celestialError) {
                            console.error(`❌ ATTEMPT ${attemptNumber}: D3-Celestial internal error:`, {
                                message: celestialError.message,
                                stack: celestialError.stack,
                                configContainer: configToUse.container,
                                actualContainerExists: !!document.getElementById(configToUse.container)
                            });

                            // If it's the "can't access property 'value'" error, try a different approach
                            if (celestialError.message && celestialError.message.includes("can't access property")) {
                                console.log(`🔄 ATTEMPT ${attemptNumber}: Detected DOM access error, trying DOM reset...`);

                                // Clear and recreate the container
                                const parent = currentContainer.parentNode;
                                const newContainer = document.createElement('div');
                                newContainer.id = 'map-container';
                                newContainer.style.position = 'relative';
                                newContainer.style.width = `${configToUse.width}px`;
                                newContainer.style.height = `${configToUse.height}px`;
                                newContainer.style.overflow = 'hidden';

                                parent.replaceChild(newContainer, currentContainer);

                                // Wait a bit and try again
                                await new Promise(resolve => setTimeout(resolve, 200));

                                try {
                                    window.Celestial.display(configToUse);
                                } catch (retryError) {
                                    console.error(`❌ ATTEMPT ${attemptNumber}: Retry after DOM reset also failed:`, retryError);
                                    throw retryError;
                                }
                            } else {
                                throw celestialError;
                            }
                        }

                        // Add a timeout to check if rendering was successful
                        return new Promise((resolve) => {
                            console.log(`⏱️ ATTEMPT ${attemptNumber}: Starting render completion check...`);

                            // Add overall timeout protection
                            const overallTimeout = setTimeout(() => {
                                console.error(`❌ ATTEMPT ${attemptNumber}: Overall timeout - render check took too long`);
                                setError('Map generation timed out - trying alternative approach...');
                                resolve(false);
                            }, 15000); // 15 second overall timeout

                            // Wait longer and check multiple times for successful rendering
                            let checkCount = 0;
                            const maxChecks = 10;
                            const checkInterval = 300; // Check every 300ms

                            const checkRendering = () => {
                                checkCount++;
                                console.log(`🔍 RENDER CHECK ${checkCount}/${maxChecks} (Attempt ${attemptNumber}): Starting...`);

                                const canvas = mapContainer.querySelector('canvas');
                                const svg = mapContainer.querySelector('svg');

                                console.log(`🔍 RENDER CHECK ${checkCount}/${maxChecks} (Attempt ${attemptNumber}):`, {
                                    hasCanvas: !!canvas,
                                    hasSvg: !!svg,
                                    canvasSize: canvas ? `${canvas.width}x${canvas.height}` : 'none',
                                    svgSize: svg ? `${svg.getAttribute('width')}x${svg.getAttribute('height')}` : 'none',
                                    containerChildren: mapContainer.children.length,
                                    checkCount
                                });

                                if (!canvas && !svg) {
                                    if (checkCount < maxChecks) {
                                        console.log(`⏳ No rendering output yet, waiting... (${checkCount}/${maxChecks})`);
                                        const checkTimeout = setTimeout(checkRendering, checkInterval);

                                        // Add safety timeout for this specific check
                                        setTimeout(() => {
                                            console.error(`❌ ATTEMPT ${attemptNumber}: Check ${checkCount} timed out, forcing next check`);
                                            clearTimeout(checkTimeout);
                                            checkRendering();
                                        }, checkInterval + 1000); // 1 second extra

                                        return;
                                    } else {
                                        console.error(`❌ ATTEMPT ${attemptNumber}: No rendering output after ${maxChecks} checks`);
                                        clearTimeout(overallTimeout);
                                        resolve(false);
                                        return;
                                    }
                                }

                                // If we have an SVG, check if it has content
                                if (svg && !canvas) {
                                    const svgElements = svg.querySelectorAll('*');
                                    const hasVisibleElements = svgElements.length > 5; // More than just basic structure

                                    console.log(`🎨 SVG CHECK ${checkCount} (Attempt ${attemptNumber}):`, {
                                        width: svg.getAttribute('width') || svg.style.width,
                                        height: svg.getAttribute('height') || svg.style.height,
                                        elementCount: svgElements.length,
                                        hasVisibleElements: hasVisibleElements
                                    });

                                    if (hasVisibleElements) {
                                        console.log(`✅ ATTEMPT ${attemptNumber}: STAR MAP RENDERED SUCCESSFULLY (SVG)`);
                                        clearTimeout(overallTimeout);
                                        setError(null);
                                        notifyMapLoaded();
                                        resolve(true);
                                        return;
                                    } else if (checkCount < maxChecks) {
                                        console.log(`⏳ SVG still empty, waiting... (${checkCount}/${maxChecks})`);
                                        const checkTimeout = setTimeout(checkRendering, checkInterval);

                                        // Add safety timeout for this specific check
                                        setTimeout(() => {
                                            console.error(`❌ ATTEMPT ${attemptNumber}: SVG check ${checkCount} timed out, forcing next check`);
                                            clearTimeout(checkTimeout);
                                            checkRendering();
                                        }, checkInterval + 1000);

                                        return;
                                    } else {
                                        console.error(`❌ ATTEMPT ${attemptNumber}: SVG empty after ${maxChecks} checks`);
                                        clearTimeout(overallTimeout);
                                        resolve(false);
                                        return;
                                    }
                                }

                                if (canvas) {
                                    // Check if canvas has content
                                    try {
                                        const ctx = canvas.getContext('2d');

                                        // Check multiple sample areas across the canvas
                                        const sampleSize = 50;
                                        const samples = [
                                            [0, 0], // top-left
                                            [canvas.width - sampleSize, 0], // top-right
                                            [0, canvas.height - sampleSize], // bottom-left
                                            [canvas.width - sampleSize, canvas.height - sampleSize], // bottom-right
                                            [Math.floor(canvas.width / 2) - sampleSize / 2, Math.floor(canvas.height / 2) - sampleSize / 2] // center
                                        ];

                                        let hasContent = false;
                                        let totalNonZeroPixels = 0;

                                        for (const [x, y] of samples) {
                                            const sampleX = Math.max(0, Math.min(x, canvas.width - sampleSize));
                                            const sampleY = Math.max(0, Math.min(y, canvas.height - sampleSize));

                                            const imageData = ctx.getImageData(sampleX, sampleY, sampleSize, sampleSize);

                                            for (let i = 0; i < imageData.data.length; i += 4) {
                                                // Check if any pixel has any color (more lenient check)
                                                const r = imageData.data[i];
                                                const g = imageData.data[i + 1];
                                                const b = imageData.data[i + 2];
                                                const a = imageData.data[i + 3];

                                                // Consider a pixel as content if it has any visible color or is not fully transparent
                                                if ((r > 0 || g > 0 || b > 0) && a > 0) {
                                                    totalNonZeroPixels++;
                                                    hasContent = true;
                                                }
                                            }

                                            if (hasContent) break; // Found content, no need to check more samples
                                        }

                                        console.log(`🎨 CANVAS CHECK ${checkCount} (Attempt ${attemptNumber}):`, {
                                            width: canvas.width,
                                            height: canvas.height,
                                            hasContent: hasContent,
                                            totalNonZeroPixels: totalNonZeroPixels,
                                            samplesChecked: hasContent ? 'found content in samples' : 'all samples empty'
                                        });

                                        if (!hasContent) {
                                            if (checkCount < maxChecks) {
                                                console.log(`⏳ Canvas still empty, waiting... (${checkCount}/${maxChecks})`);
                                                const checkTimeout = setTimeout(checkRendering, checkInterval);

                                                // Add safety timeout for this specific check
                                                setTimeout(() => {
                                                    console.error(`❌ ATTEMPT ${attemptNumber}: Canvas check ${checkCount} timed out, forcing next check`);
                                                    clearTimeout(checkTimeout);
                                                    checkRendering();
                                                }, checkInterval + 1000);

                                                return;
                                            } else {
                                                // Check if canvas has reasonable dimensions - maybe content detection failed but map is there
                                                if (canvas.width >= 400 && canvas.height >= 400) {
                                                    console.log(`⚠️ ATTEMPT ${attemptNumber}: Canvas content detection failed but canvas has proper dimensions (${canvas.width}x${canvas.height}), considering as success`);
                                                    clearTimeout(overallTimeout);
                                                    setError(null);
                                                    notifyMapLoaded();
                                                    resolve(true);
                                                    return;
                                                }

                                                console.error(`❌ ATTEMPT ${attemptNumber}: Canvas empty after ${maxChecks} checks`);

                                                // Try forcing a redraw before giving up
                                                if (window.Celestial && typeof window.Celestial.redraw === 'function') {
                                                    console.log(`🔄 ATTEMPT ${attemptNumber}: Forcing redraw before final check...`);
                                                    try {
                                                        window.Celestial.redraw();
                                                        // Give it one more chance after forced redraw
                                                        setTimeout(() => {
                                                            const ctx2 = canvas.getContext('2d');

                                                            // Use the same improved checking logic after redraw
                                                            const sampleSize = 50;
                                                            const samples = [
                                                                [0, 0], // top-left
                                                                [canvas.width - sampleSize, 0], // top-right
                                                                [0, canvas.height - sampleSize], // bottom-left
                                                                [canvas.width - sampleSize, canvas.height - sampleSize], // bottom-right
                                                                [Math.floor(canvas.width / 2) - sampleSize / 2, Math.floor(canvas.height / 2) - sampleSize / 2] // center
                                                            ];

                                                            let hasContentAfterRedraw = false;

                                                            for (const [x, y] of samples) {
                                                                const sampleX = Math.max(0, Math.min(x, canvas.width - sampleSize));
                                                                const sampleY = Math.max(0, Math.min(y, canvas.height - sampleSize));

                                                                const imageData2 = ctx2.getImageData(sampleX, sampleY, sampleSize, sampleSize);

                                                                for (let i = 0; i < imageData2.data.length; i += 4) {
                                                                    const r = imageData2.data[i];
                                                                    const g = imageData2.data[i + 1];
                                                                    const b = imageData2.data[i + 2];
                                                                    const a = imageData2.data[i + 3];

                                                                    if ((r > 0 || g > 0 || b > 0) && a > 0) {
                                                                        hasContentAfterRedraw = true;
                                                                        break;
                                                                    }
                                                                }

                                                                if (hasContentAfterRedraw) break;
                                                            }

                                                            if (hasContentAfterRedraw) {
                                                                console.log(`✅ ATTEMPT ${attemptNumber}: SUCCESS after forced redraw!`);
                                                                clearTimeout(overallTimeout);
                                                                setError(null);
                                                                notifyMapLoaded();
                                                                resolve(true);
                                                            } else {
                                                                console.error(`❌ ATTEMPT ${attemptNumber}: Still empty after forced redraw`);
                                                                clearTimeout(overallTimeout);
                                                                resolve(false);
                                                            }
                                                        }, 500);
                                                        return;
                                                    } catch (redrawError) {
                                                        console.error(`❌ ATTEMPT ${attemptNumber}: Redraw failed:`, redrawError);
                                                        resolve(false);
                                                        return;
                                                    }
                                                }

                                                resolve(false);
                                                return;
                                            }
                                        }
                                    } catch (canvasError) {
                                        console.error(`❌ ATTEMPT ${attemptNumber}: Canvas check error:`, canvasError);
                                        if (checkCount < maxChecks) {
                                            const checkTimeout = setTimeout(checkRendering, checkInterval);

                                            // Add safety timeout for this specific check
                                            setTimeout(() => {
                                                console.error(`❌ ATTEMPT ${attemptNumber}: Error check ${checkCount} timed out, forcing next check`);
                                                clearTimeout(checkTimeout);
                                                checkRendering();
                                            }, checkInterval + 1000);

                                            return;
                                        } else {
                                            clearTimeout(overallTimeout);
                                            resolve(false);
                                            return;
                                        }
                                    }
                                }

                                // If we get here, rendering was successful
                                console.log(`✅ ATTEMPT ${attemptNumber}: STAR MAP RENDERED SUCCESSFULLY`);
                                clearTimeout(overallTimeout);
                                setError(null);
                                notifyMapLoaded();
                                resolve(true);
                            };

                            // Start checking after initial delay
                            setTimeout(checkRendering, 500);
                        });

                    } catch (displayError) {
                        console.error(`❌ ATTEMPT ${attemptNumber}: CELESTIAL.DISPLAY ERROR:`, displayError);
                        return false;
                    }
                };

                // Try main configuration first
                const success = await attemptDisplay(config, 1);

                if (!success) {
                    console.log('🔄 FIRST ATTEMPT FAILED - Trying minimal configuration...');

                    // Clear container and try minimal config
                    mapContainer.innerHTML = '';

                    // Create minimal configuration for fallback
                    const minimalConfig = {
                        width: containerWidth || 600,
                        height: containerWidth || 600,
                        container: "map-container",
                        datapath: `${window.location.origin}/d3-celestial-data/`,
                        projection: "stereographic",
                        background: { fill: "transparent" },
                        date: properDate,
                        geopos: [latitude, longitude],
                        transform: "equatorial",
                        interactive: false,
                        form: false,
                        controls: false,
                        transition: false,
                        adaptable: false,
                        animationDuration: 0,
                        stars: {
                            show: true,
                            limit: 6,
                            colors: true,
                            style: { fill: "#ffffff", opacity: 0.8 },
                            data: "stars.6.json"
                        },
                        constellations: {
                            show: showConstellations,
                            names: false, // Disable names for minimal config
                            lines: showConstellations
                        },
                        dsos: { show: false }, // Disable for minimal config
                        lines: {
                            graticule: { show: false },
                            equatorial: { show: false },
                            ecliptic: { show: false }
                        },
                        mw: { show: false }
                    };

                    console.log('🔧 MINIMAL CONFIG:', JSON.stringify(minimalConfig, (key, value) => {
                        if (key === 'date' && value instanceof Date) {
                            return value.toISOString();
                        }
                        return value;
                    }, 2));

                    const minimalSuccess = await attemptDisplay(minimalConfig, 2);

                    if (!minimalSuccess) {
                        console.error('❌ ALL ATTEMPTS FAILED - Both full and minimal configurations failed');
                        setError('Star map failed to render - trying alternative approach...');

                        // Final fallback - try absolutely minimal config
                        mapContainer.innerHTML = '';

                        try {
                            // Try the absolute most basic configuration possible
                            const ultraMinimalConfig = {
                                width: 600,
                                height: 600,
                                container: "map-container",
                                datapath: `${window.location.origin}/d3-celestial-data/`,
                                projection: "stereographic",
                                date: properDate,
                                geopos: [latitude, longitude],
                                interactive: false,
                                form: false,
                                controls: false,
                                stars: {
                                    show: true,
                                    limit: 6,
                                    data: "stars.6.json"
                                }
                            };

                            console.log('🔧 ULTRA-MINIMAL CONFIG:', ultraMinimalConfig);
                            const ultraResult = await attemptDisplay(ultraMinimalConfig, 3);

                            if (!ultraResult) {
                                // Last resort - try the most minimal configuration possible
                                console.log('🔧 FINAL ATTEMPT: Using absolute minimal config...');
                                const absoluteMinimal = {
                                    container: "map-container",
                                    datapath: `${window.location.origin}/d3-celestial-data/`,
                                    width: 600,
                                    height: 600,
                                    date: properDate,
                                    geopos: [latitude, longitude],
                                    stars: { show: true, limit: 4, data: "stars.6.json" }
                                };

                                console.log('🔧 ABSOLUTE MINIMAL CONFIG:', absoluteMinimal);
                                const absoluteResult = await attemptDisplay(absoluteMinimal, 4);

                                if (!absoluteResult) {
                                    // Final fallback - try forcing different rendering methods
                                    console.log('🔧 EMERGENCY FALLBACK: Trying different approaches...');

                                    // Try clearing everything and waiting longer
                                    mapContainer.innerHTML = '';
                                    await new Promise(resolve => setTimeout(resolve, 1000));

                                    // Try with SVG output instead of canvas
                                    const svgConfig = {
                                        ...absoluteMinimal,
                                        canvas: false, // Force SVG output
                                        interactive: false,
                                        form: false
                                    };

                                    console.log('🔧 SVG FALLBACK CONFIG:', svgConfig);
                                    await attemptDisplay(svgConfig, 5);
                                }
                            }
                        } catch (finalError) {
                            console.error('❌ FINAL FALLBACK FAILED:', finalError);
                            setError('Unable to render star map with any configuration');
                            notifyMapLoaded();
                        }
                    }
                }

            } catch (configError) {
                console.error('❌ CONFIGURATION ERROR:', configError);
                setError(`Configuration error: ${configError.message}`);
                notifyMapLoaded();
            }
        };

    }, [date, location, size, regenerate, celestialAttempts, notifyMapLoaded, showStars, showConstellations, showConstellationNames, showDeepSkyObjects, showEcliptic, showGrid]);

    // Create a debounced version for smoother updates - defined after generateStarMapInternal
    const debouncedGenerateStarMap = useCallback((immediate = false, forcedDate = null, forcedLocation = null) => {
        // Use current props if no forced values provided - this prevents stale closure
        const currentDate = forcedDate || date;
        const currentLocation = forcedLocation || location;

        console.log('🔄 DEBUG: debouncedGenerateStarMap called', {
            immediate,
            forcedDate,
            forcedLocation,
            currentDate,
            currentLocation,
            originalDate: date,
            originalLocation: location
        });

        // Clear any existing timeout
        if (updateTimeoutRef.current) {
            clearTimeout(updateTimeoutRef.current);
        }

        const executeGeneration = () => {
            try {
                console.log('🚀 DEBUG: About to call generateStarMapInternal');
                generateStarMapInternal(currentDate, currentLocation);

                // Set a fallback timeout to reset initialization state if generation fails silently
                setTimeout(() => {
                    if (isInitializingRef.current) {
                        console.warn('⚠️ Generation timeout: Map generation took too long, resetting initialization state');
                        isInitializingRef.current = false;
                        setError('Map generation timeout. Please try refreshing the page.');
                        notifyMapLoaded();
                    }
                }, 10000); // 10 second timeout
            } catch (err) {
                console.error('❌ Error in debouncedGenerateStarMap:', err);
                isInitializingRef.current = false;
                setError(`Map generation error: ${err.message}`);
                notifyMapLoaded();
            }
        };

        if (immediate) {
            // Execute immediately for initial load - pass current values to prevent stale closure
            executeGeneration();
        } else {
            // Debounce for subsequent updates
            updateTimeoutRef.current = setTimeout(executeGeneration, 150); // 150ms debounce for smooth updates
        }
    }, [date, location, generateStarMapInternal, notifyMapLoaded]); // Added generateStarMapInternal dependency

    // Export the debounced version as generateStarMap for compatibility
    const generateStarMap = debouncedGenerateStarMap;

    // Now we can use these functions in our effects
    // Update visibility settings when display options change
    useEffect(() => {
        // Only update existing map if:
        // 1. Celestial is ready
        // 2. We don't need to regenerate the map
        if (isCelestialReady()) {
            // Create configuration object just for visibility settings
            const updatedSettings = {
                stars: {
                    show: showStars
                },
                constellations: {
                    lines: showConstellations,
                    names: showConstellationNames,
                    // Include the lineStyle to maintain our constellation line appearance
                    lineStyle: { stroke: "#8eb4e3", opacity: 0.3, width: 1 }
                },
                dsos: {
                    show: showDeepSkyObjects
                },
                lines: {
                    graticule: { show: showGrid, stroke: "#9090a0", width: 1, opacity: 0.3 },
                    equatorial: { show: showGrid, stroke: "#9090a0", width: 1, opacity: 0.3 },
                    ecliptic: { show: showEcliptic, stroke: "#e6b366", width: 1.5, opacity: 0.5 }
                }
            };

            console.log('DEBUG: Applying visibility settings:', {
                showGrid,
                showEcliptic,
                settings: updatedSettings.lines
            });

            // Apply new settings directly and redraw
            if (window.Celestial && typeof window.Celestial.apply === 'function') {
                console.log('Applying visibility settings without regenerating:', updatedSettings);
                window.Celestial.apply(updatedSettings);
                window.Celestial.redraw();

                // Debug: Check what we have after redraw
                if (window.Celestial.settings && window.Celestial.settings.lines) {
                    console.log('After redraw, lines settings are:', window.Celestial.settings.lines);
                }

                // Try direct DOM inspection as well
                setTimeout(() => {
                    const svg = document.querySelector('.starmap-canvas svg');
                    if (svg) {
                        console.log('SVG elements counts:', {
                            'gridlines': svg.querySelectorAll('path.gridline, path.graticule, path.equatorial').length,
                            'ecliptic': svg.querySelectorAll('path.ecliptic').length
                        });

                        // Force-add the grid lines and ecliptic if they don't exist
                        if (showGrid && svg.querySelectorAll('path.gridline, path.graticule, path.equatorial').length === 0) {
                            console.log('Forcing grid lines to be added');
                            forceAddGridLines(svg);
                        }

                        if (showEcliptic && svg.querySelectorAll('path.ecliptic').length === 0) {
                            console.log('Forcing ecliptic line to be added');
                            forceAddEclipticLine(svg);
                        }
                    }
                }, 500);
            }
        }
    }, [showStars, showConstellations, showConstellationNames,
        showGrid, showDeepSkyObjects, showEcliptic, regenerate]);

    // Cleanup effect to prevent memory leaks
    useEffect(() => {
        return () => {
            console.log('StarMapGenerator unmounting, performing comprehensive cleanup');

            // Clear any pending debounced updates
            if (updateTimeoutRef.current) {
                clearTimeout(updateTimeoutRef.current);
            }

            // Comprehensive celestial cleanup
            if (window.Celestial) {
                try {
                    // Clear display
                    if (window.Celestial.clear) {
                        window.Celestial.clear();
                    }

                    // Remove all celestial event listeners
                    if (window.Celestial.removeAllListeners) {
                        window.Celestial.removeAllListeners();
                    }

                    // Clear canvas contexts and remove from DOM
                    const canvases = document.querySelectorAll('#map-container canvas');
                    canvases.forEach(canvas => {
                        const ctx = canvas.getContext('2d');
                        if (ctx) {
                            ctx.clearRect(0, 0, canvas.width, canvas.height);
                        }
                        if (canvas.parentNode) {
                            canvas.parentNode.removeChild(canvas);
                        }
                    });

                } catch (e) {
                    console.warn('Error during celestial cleanup:', e);
                }
            }

            // Clear the map container and remove all child elements
            if (mapContainerRef.current) {
                // Remove all event listeners from child elements
                const allElements = mapContainerRef.current.querySelectorAll('*');
                allElements.forEach(element => {
                    if (element.removeEventListener) {
                        // Remove common event listeners
                        ['click', 'mouseover', 'mouseout', 'mousedown', 'mouseup', 'mousemove'].forEach(eventType => {
                            element.removeEventListener(eventType, () => { });
                        });
                    }
                });

                // Clear innerHTML
                mapContainerRef.current.innerHTML = '';
            }

            // Clear map container from DOM if it exists
            const mapContainer = document.getElementById('map-container');
            if (mapContainer && mapContainer.parentNode) {
                mapContainer.parentNode.removeChild(mapContainer);
            }

            // Reset refs
            isInitializingRef.current = false;
            hasInitializedRef.current = false;
            mountRef.current = false;

            // Call original cleanup function
            performMemoryCleanup();

            // Force garbage collection hint (if available in dev tools)
            if (window.gc) {
                window.gc();
            }
        };
    }, [performMemoryCleanup]);

    // Helper function to manually add grid lines if they're missing
    const forceAddGridLines = (svg) => {
        if (!svg) return;

        // Create a simple grid pattern (meridians and parallels)
        const svgNS = "http://www.w3.org/2000/svg";

        // Create meridians (vertical lines)
        for (let i = 0; i < 24; i++) {
            const line = document.createElementNS(svgNS, "path");
            line.setAttribute("class", "graticule");
            line.setAttribute("d", `M${400 + 300 * Math.cos(i * Math.PI / 12)},${400 + 300 * Math.sin(i * Math.PI / 12)} A300,300 0 0,1 ${400 + 300 * Math.cos((i + 12) * Math.PI / 12)},${400 + 300 * Math.sin((i + 12) * Math.PI / 12)}`);
            line.setAttribute("stroke", "#8989b3");
            line.setAttribute("stroke-width", "1");
            line.setAttribute("opacity", "0.6");
            line.setAttribute("fill", "none");
            svg.appendChild(line);
        }

        // Create parallels (horizontal lines)
        for (let i = 1; i < 6; i++) {
            const radius = 300 * (i / 6);
            const circle = document.createElementNS(svgNS, "circle");
            circle.setAttribute("class", "graticule");
            circle.setAttribute("cx", "400");
            circle.setAttribute("cy", "400");
            circle.setAttribute("r", radius.toString());
            circle.setAttribute("stroke", "#8989b3");
            circle.setAttribute("stroke-width", "1");
            circle.setAttribute("opacity", "0.6");
            circle.setAttribute("fill", "none");
            svg.appendChild(circle);
        }
    };

    // Helper function to manually add ecliptic line if it's missing
    const forceAddEclipticLine = (svg) => {
        if (!svg) return;

        const svgNS = "http://www.w3.org/2000/svg";
        const ecliptic = document.createElementNS(svgNS, "ellipse");
        ecliptic.setAttribute("class", "ecliptic");
        ecliptic.setAttribute("cx", "400");
        ecliptic.setAttribute("cy", "400");
        ecliptic.setAttribute("rx", "350");
        ecliptic.setAttribute("ry", "330");
        ecliptic.setAttribute("transform", "rotate(23.5 400 400)"); // Ecliptic is tilted at 23.5 degrees
        ecliptic.setAttribute("stroke", "#dda74f");
        ecliptic.setAttribute("stroke-width", "1.5");
        ecliptic.setAttribute("opacity", "0.7");
        ecliptic.setAttribute("fill", "none");
        svg.appendChild(ecliptic);
    };

    // Optimize the initializeStarMap function - moved before useEffect to fix initialization order
    const initializeStarMap = useCallback(() => {
        console.log('🚀 DEBUG: initializeStarMap called', {
            isInitializing: isInitializingRef.current,
            hasInitialized: hasInitializedRef.current,
            date,
            location,
            regenerate,
            lastDate: lastDateRef.current,
            lastLocation: lastLocationRef.current
        });

        // Prevent duplicate initialization
        if (isInitializingRef.current) {
            console.log('⏸️ Star map initialization already in progress, skipping');
            return true;
        }

        // Check if already successfully initialized with same data
        if (hasInitializedRef.current &&
            lastDateRef.current === date &&
            lastLocationRef.current === location &&
            !regenerate) {
            console.log('⏸️ Star map already initialized with same data, skipping');
            return true;
        }

        console.log('Initializing star map...');
        isInitializingRef.current = true;

        try {
            // Get the container width
            const containerWidth = mapContainerRef.current?.clientWidth || 800;
            console.log('Map container width:', containerWidth);

            // Make sure mapContainerRef has a valid DOM element
            if (!mapContainerRef.current) {
                console.error('Map container reference is not available');
                setError('Unable to find map container. Using simplified star map instead.');
                isInitializingRef.current = false;
                return false;
            }

            // Find or create the map element container
            let mapElement = document.getElementById('map-container');

            // Only create a new container if it doesn't exist
            if (!mapElement) {
                console.log('Creating map container in initializeStarMap');
                mapElement = document.createElement('div');
                mapElement.id = 'map-container';
                mapElement.style.width = '100%';
                mapElement.style.height = '100%';
                mapElement.style.position = 'absolute';
                mapElement.style.top = '0';
                mapElement.style.left = '0';
                mapElement.style.display = 'block';
                mapElement.style.overflow = 'hidden';

                // Clear parent container and add the map element
                mapContainerRef.current.innerHTML = '';
                mapContainerRef.current.appendChild(mapElement);

                console.log('Map container created in initializeStarMap:', {
                    elementId: mapElement.id,
                    parentElement: mapElement.parentElement?.tagName,
                    parentClass: mapElement.parentElement?.className
                });
            } else {
                console.log('Map container already exists:', {
                    elementId: mapElement.id,
                    hasParent: !!mapElement.parentElement,
                    innerHTML: mapElement.innerHTML.length > 0 ? 'has content' : 'empty'
                });

                // Clear existing content to ensure clean slate
                mapElement.innerHTML = '';
            }

            console.log('Map container prepared, generating map');

            // Generate the star map - pass current values to prevent stale closure
            debouncedGenerateStarMap(true, date, location);
            return true;
        } catch (err) {
            console.error('Error during map initialization:', err);
            setError('Error initializing star map. Using simplified version instead.');
            isInitializingRef.current = false;
            return false;
        }
    }, [debouncedGenerateStarMap, date, location, regenerate]);

    // Initialize Celestial with default settings - moved after initializeStarMap definition
    useEffect(() => {
        console.log('Init effect with default settings');
        // Prevent multiple simultaneous initializations
        if (isInitializingRef.current || hasInitializedRef.current) {
            console.log('⏸️ Skipping init effect - already initialized or in progress');
            return;
        }

        if (isCelestialReady()) {
            initializeStarMap();
        }
    }, []); // EMPTY dependency array - run only once on mount

    // Listen for celestial-ready event
    useEffect(() => {
        const handleCelestialReady = () => {
            console.log('Received celestial-ready event');
            // Only initialize if not already initializing or initialized
            if (!isInitializingRef.current && !hasInitializedRef.current) {
                if (initializeStarMap()) {
                    console.log('Star map initialized successfully after celestial-ready event');
                }
            } else {
                console.log('⏸️ Skipping celestial-ready initialization - already handled');
            }
        };

        // Listen for the custom event
        window.addEventListener('celestial-ready', handleCelestialReady);

        // Also check if celestial is already loaded - But ONLY on first mount
        if (isCelestialReady() && !mountRef.current && !hasInitializedRef.current) {
            console.log('Celestial already loaded on mount, initializing');
            initializeStarMap();
            mountRef.current = true; // Mark as mounted
        }

        return () => {
            window.removeEventListener('celestial-ready', handleCelestialReady);
        };
    }, []); // <-- EMPTY dependency array to ensure it only runs once on mount

    // Listen for data loaded event
    useEffect(() => {
        const handleDataLoaded = () => {
            console.log('Received celestial-data-loaded event');
            // Only initialize if not already initialized
            if (!isInitializingRef.current && !hasInitializedRef.current) {
                // Wait a short moment to ensure everything is ready
                setTimeout(() => {
                    if (isCelestialReady()) {
                        console.log('Initializing after data loaded event');
                        initializeStarMap();
                    }
                }, 300);
            } else {
                console.log('⏸️ Skipping data-loaded initialization - already handled');
            }
        };

        window.addEventListener('celestial-data-loaded', handleDataLoaded);

        return () => {
            window.removeEventListener('celestial-data-loaded', handleDataLoaded);
        };
    }, []); // EMPTY dependency array - only set up listener once

    // Optimize the useEffect for changes in date and location - with fast updates
    useEffect(() => {
        if (date && location && isCelestialReady()) {
            // Check if we need to update
            const locationChanged = lastLocationRef.current !== location;
            const dateChanged = lastDateRef.current !== date;

            // Add explicit date debugging
            console.log('Date change check:', {
                previousDate: lastDateRef.current,
                newDate: date,
                dateChanged,
                dateObj: new Date(date),
                previousDateObj: lastDateRef.current ? new Date(lastDateRef.current) : null,
                regenerateFlag: regenerate,
                isInitialized: hasInitializedRef.current
            });

            // Only proceed if there are actual changes or explicit regeneration needed
            // For regenerate flag, always proceed regardless of initialization state
            // For date changes, also proceed even if initialized (for efficient date updates)
            console.log('🔄 DEBUG: Checking regeneration conditions', {
                regenerate,
                locationChanged,
                dateChanged,
                hasInitialized: hasInitializedRef.current,
                isInitializing: isInitializingRef.current,
                shouldRegenerate: regenerate || locationChanged || dateChanged
            });

            if (regenerate || locationChanged || dateChanged) {
                // CRITICAL: Prevent multiple simultaneous updates that cause infinite loops
                if (isInitializingRef.current) {
                    console.log('⏸️ UPDATE BLOCKED: Initialization already in progress, preventing infinite loop', {
                        regenerate,
                        locationChanged,
                        dateChanged,
                        currentlyInitializing: isInitializingRef.current,
                        timestamp: new Date().toISOString()
                    });
                    return;
                }

                // Set initialization flag immediately to prevent race conditions
                isInitializingRef.current = true;
                console.log('🔒 Setting initialization flag to prevent race conditions');

                console.log('🔥 MAP UPDATE TRIGGERED - Map needs to update due to:', {
                    regenerate,
                    locationChanged,
                    dateChanged,
                    isInitialized: hasInitializedRef.current,
                    timestamp: new Date().toISOString()
                });

                // DEBUG: Check current DOM state before any changes
                const beforeContainer = document.getElementById('map-container');
                const beforeCanvas = beforeContainer?.querySelector('canvas');
                const beforeSvg = beforeContainer?.querySelector('svg');
                console.log('🔍 DOM STATE BEFORE UPDATE:', {
                    containerExists: !!beforeContainer,
                    canvasExists: !!beforeCanvas,
                    svgExists: !!beforeSvg,
                    canvasSize: beforeCanvas ? `${beforeCanvas.width}x${beforeCanvas.height}` : 'none',
                    containerChildren: beforeContainer?.children.length || 0
                });

                // Only reset initialization state for full regeneration or location changes
                if (regenerate || locationChanged) {
                    hasInitializedRef.current = false;
                    console.log('🔄 Resetting initialization state for full regeneration');
                } else if (dateChanged) {
                    console.log('🔄 Keeping initialization state for date-only change');
                }

                // Update references
                lastDateRef.current = date;
                lastLocationRef.current = location;

                // Add a safety timeout to reset initialization flag if something goes wrong
                const safetyTimeout = setTimeout(() => {
                    if (isInitializingRef.current) {
                        console.warn('⚠️ Safety timeout: Resetting initialization flag after 15 seconds');
                        isInitializingRef.current = false;
                        hasInitializedRef.current = true;
                    }
                }, 15000);

                // Store the timeout reference so we can clear it on success
                const timeoutRef = { current: safetyTimeout };

                // Handle date changes efficiently
                if (dateChanged && !regenerate && !locationChanged) {
                    console.log('DATE CHANGED - Analyzing date difference for appropriate update method');

                    try {
                        // Parse dates and calculate the difference
                        const newDateObj = new Date(date);
                        const oldDateObj = lastDateRef.current ? new Date(lastDateRef.current) : new Date();

                        // Calculate difference in years
                        const yearsDifference = Math.abs(newDateObj.getFullYear() - oldDateObj.getFullYear());

                        // Calculate difference in months (for seasonal changes)
                        const monthsDifference = Math.abs(
                            (newDateObj.getFullYear() - oldDateObj.getFullYear()) * 12 +
                            (newDateObj.getMonth() - oldDateObj.getMonth())
                        );

                        console.log('Date change analysis:', {
                            oldDate: lastDateRef.current,
                            newDate: date,
                            yearsDifference,
                            monthsDifference,
                            isLargeDateChange: yearsDifference > 2 || monthsDifference > 6
                        });

                        // For large date changes (>2 years or >6 months), force complete rebuild
                        if (yearsDifference > 2 || monthsDifference > 6) {
                            console.log('LARGE DATE CHANGE DETECTED - Using complete rebuild for astronomical accuracy');

                            // Get current location
                            let currentGeopos = [51.5074, -0.1278]; // Default London
                            if (window.Celestial.settings && window.Celestial.settings.geopos) {
                                currentGeopos = window.Celestial.settings.geopos;
                            }

                            // Force complete regeneration with new date
                            const completeConfig = {
                                width: mapContainerRef.current?.clientWidth || 600,
                                height: mapContainerRef.current?.clientWidth || 600,
                                container: "map-container",
                                datapath: `${window.location.origin}/d3-celestial-data/`,
                                projection: "stereographic",
                                background: {
                                    fill: "transparent",
                                    stroke: "transparent",
                                    opacity: 0,
                                    width: 0
                                },
                                date: newDateObj,
                                center: [0, 0, 0],
                                follow: "zenith",
                                geopos: currentGeopos,
                                transform: "equatorial",
                                transition: true,
                                adaptable: true,
                                animationDuration: 800, // Longer animation to show dramatic change
                                interactive: false,
                                form: false,
                                controls: false,
                                stars: {
                                    show: showStars,
                                    limit: 6,
                                    colors: true,
                                    style: { fill: "#ffffff", opacity: 0.8 },
                                    designation: false,
                                    propername: false
                                },
                                constellations: {
                                    show: showConstellations,
                                    names: showConstellationNames,
                                    lines: showConstellations,
                                    linestyle: { stroke: "#cccccc", width: 1, opacity: 0.7 }
                                },
                                lines: {
                                    graticule: { show: showGrid, stroke: "#8989b3", width: 1, opacity: 0.6 },
                                    equatorial: { show: showGrid, stroke: "#8989b3", width: 1, opacity: 0.6 },
                                    ecliptic: { show: showEcliptic, stroke: "#dda74f", width: 1.5, opacity: 0.7 }
                                },
                                dsos: {
                                    show: showDeepSkyObjects,
                                    limit: 6,
                                    colors: true,
                                    style: { fill: "#cccccc", opacity: 0.7 }
                                }
                            };

                            console.log('Using complete Celestial.display() for dramatic date change');
                            window.Celestial.display(completeConfig);

                            // Add proper validation for large date changes
                            setTimeout(() => {
                                const mapContainer = document.getElementById('map-container');
                                const canvas = mapContainer?.querySelector('canvas');
                                const svg = mapContainer?.querySelector('svg');

                                if (!canvas && !svg) {
                                    console.error('❌ Large date change: No rendering output detected');
                                    setError('Date change failed to render - trying alternative approach...');
                                    // Clear timeout before fallback
                                    if (timeoutRef && timeoutRef.current) {
                                        clearTimeout(timeoutRef.current);
                                    }
                                    // Fallback to full regeneration
                                    initializeStarMap();
                                    return;
                                }

                                // Validate canvas content if canvas exists
                                if (canvas) {
                                    try {
                                        const ctx = canvas.getContext('2d');
                                        const sampleSize = 50;
                                        const samples = [
                                            [0, 0],
                                            [canvas.width - sampleSize, 0],
                                            [0, canvas.height - sampleSize],
                                            [canvas.width - sampleSize, canvas.height - sampleSize],
                                            [Math.floor(canvas.width / 2) - sampleSize / 2, Math.floor(canvas.height / 2) - sampleSize / 2]
                                        ];

                                        let hasContent = false;
                                        for (const [x, y] of samples) {
                                            const sampleX = Math.max(0, Math.min(x, canvas.width - sampleSize));
                                            const sampleY = Math.max(0, Math.min(y, canvas.height - sampleSize));
                                            const imageData = ctx.getImageData(sampleX, sampleY, sampleSize, sampleSize);

                                            for (let i = 0; i < imageData.data.length; i += 4) {
                                                const r = imageData.data[i];
                                                const g = imageData.data[i + 1];
                                                const b = imageData.data[i + 2];
                                                const a = imageData.data[i + 3];

                                                if ((r > 0 || g > 0 || b > 0) && a > 0) {
                                                    hasContent = true;
                                                    break;
                                                }
                                            }
                                            if (hasContent) break;
                                        }

                                        if (!hasContent && canvas.width >= 400 && canvas.height >= 400) {
                                            console.log('⚠️ Large date change: Canvas content detection failed but canvas has proper dimensions, considering as success');
                                            hasContent = true;
                                        }

                                        if (!hasContent) {
                                            console.error('❌ Large date change: Canvas appears empty');
                                            setError('Date change failed to render - trying alternative approach...');
                                            // Clear timeout before fallback
                                            if (timeoutRef && timeoutRef.current) {
                                                clearTimeout(timeoutRef.current);
                                            }
                                            initializeStarMap();
                                            return;
                                        }
                                    } catch (err) {
                                        console.error('❌ Large date change: Canvas validation error:', err);
                                    }
                                }

                                console.log('✅ Large date change completed successfully');
                                setError(null);
                                notifyMapLoaded(timeoutRef);
                            }, 1000);

                            return;
                        }

                        // For small date changes, use efficient update
                        if (!locationChanged && window.Celestial && window.Celestial.apply) {
                            console.log('🎯 SMALL DATE CHANGE - Using efficient astronomical update', {
                                timestamp: new Date().toISOString(),
                                oldDate: lastDateRef.current,
                                newDate: date
                            });

                            // Get current location from existing map or use default
                            let currentGeopos = [51.5074, -0.1278]; // Default London
                            if (window.Celestial.settings && window.Celestial.settings.geopos) {
                                currentGeopos = window.Celestial.settings.geopos;
                            }

                            // DEBUG: Check DOM state before Celestial.apply()
                            const beforeApply = document.getElementById('map-container');
                            const beforeApplyCanvas = beforeApply?.querySelector('canvas');
                            console.log('🔍 DOM STATE BEFORE Celestial.apply():', {
                                containerExists: !!beforeApply,
                                canvasExists: !!beforeApplyCanvas,
                                canvasSize: beforeApplyCanvas ? `${beforeApplyCanvas.width}x${beforeApplyCanvas.height}` : 'none'
                            });

                            // Apply efficient astronomical update for small changes
                            console.log('📡 CALLING Celestial.apply() with config:', {
                                date: newDateObj.toISOString(),
                                geopos: currentGeopos,
                                timestamp: new Date().toISOString()
                            });

                            window.Celestial.apply({
                                date: newDateObj,
                                geopos: currentGeopos,
                                stars: {
                                    show: showStars,
                                    limit: 6,
                                    colors: true,
                                    style: { fill: "#ffffff", opacity: 0.8 }
                                },
                                constellations: {
                                    show: showConstellations,
                                    names: showConstellationNames,
                                    lines: showConstellations
                                },
                                transition: true,
                                adaptable: true,
                                animationDuration: 400
                            });

                            console.log('✅ Celestial.apply() completed');

                            // DEBUG: Check DOM state immediately after Celestial.apply()
                            const afterApply = document.getElementById('map-container');
                            const afterApplyCanvas = afterApply?.querySelector('canvas');
                            console.log('🔍 DOM STATE IMMEDIATELY AFTER Celestial.apply():', {
                                containerExists: !!afterApply,
                                canvasExists: !!afterApplyCanvas,
                                canvasSize: afterApplyCanvas ? `${afterApplyCanvas.width}x${afterApplyCanvas.height}` : 'none'
                            });

                            // Force complete redraw to recalculate star positions
                            console.log('📡 CALLING Celestial.redraw()');
                            window.Celestial.redraw();
                            console.log('✅ Celestial.redraw() completed');

                            // DEBUG: Check DOM state immediately after redraw
                            const afterRedraw = document.getElementById('map-container');
                            const afterRedrawCanvas = afterRedraw?.querySelector('canvas');
                            console.log('🔍 DOM STATE IMMEDIATELY AFTER Celestial.redraw():', {
                                containerExists: !!afterRedraw,
                                canvasExists: !!afterRedrawCanvas,
                                canvasSize: afterRedrawCanvas ? `${afterRedrawCanvas.width}x${afterRedrawCanvas.height}` : 'none'
                            });

                            console.log('Efficient astronomical date update completed', {
                                oldDate: lastDateRef.current,
                                newDate: date,
                                location: currentGeopos
                            });

                            // Add proper validation for small date changes
                            setTimeout(() => {
                                const mapContainer = document.getElementById('map-container');
                                const canvas = mapContainer?.querySelector('canvas');
                                const svg = mapContainer?.querySelector('svg');

                                if (!canvas && !svg) {
                                    console.error('❌ Small date change: No rendering output detected');
                                    setError('Date change failed to render - trying alternative approach...');
                                    // Clear timeout before fallback
                                    if (timeoutRef && timeoutRef.current) {
                                        clearTimeout(timeoutRef.current);
                                    }
                                    // Fallback to full regeneration
                                    initializeStarMap();
                                    return;
                                }

                                // Validate canvas content if canvas exists
                                if (canvas) {
                                    try {
                                        const ctx = canvas.getContext('2d');
                                        const sampleSize = 50;
                                        const samples = [
                                            [0, 0],
                                            [canvas.width - sampleSize, 0],
                                            [0, canvas.height - sampleSize],
                                            [canvas.width - sampleSize, canvas.height - sampleSize],
                                            [Math.floor(canvas.width / 2) - sampleSize / 2, Math.floor(canvas.height / 2) - sampleSize / 2]
                                        ];

                                        let hasContent = false;
                                        for (const [x, y] of samples) {
                                            const sampleX = Math.max(0, Math.min(x, canvas.width - sampleSize));
                                            const sampleY = Math.max(0, Math.min(y, canvas.height - sampleSize));
                                            const imageData = ctx.getImageData(sampleX, sampleY, sampleSize, sampleSize);

                                            for (let i = 0; i < imageData.data.length; i += 4) {
                                                const r = imageData.data[i];
                                                const g = imageData.data[i + 1];
                                                const b = imageData.data[i + 2];
                                                const a = imageData.data[i + 3];

                                                if ((r > 0 || g > 0 || b > 0) && a > 0) {
                                                    hasContent = true;
                                                    break;
                                                }
                                            }
                                            if (hasContent) break;
                                        }

                                        if (!hasContent && canvas.width >= 400 && canvas.height >= 400) {
                                            console.log('⚠️ Small date change: Canvas content detection failed but canvas has proper dimensions, considering as success');
                                            hasContent = true;
                                        }

                                        if (!hasContent) {
                                            console.error('❌ Small date change: Canvas appears empty');
                                            setError('Date change failed to render - trying alternative approach...');
                                            // Clear timeout before fallback
                                            if (timeoutRef && timeoutRef.current) {
                                                clearTimeout(timeoutRef.current);
                                            }
                                            initializeStarMap();
                                            return;
                                        }
                                    } catch (err) {
                                        console.error('❌ Small date change: Canvas validation error:', err);
                                    }
                                }

                                console.log('✅ Small date change completed successfully - starting monitoring');
                                setError(null);
                                notifyMapLoaded(timeoutRef);

                                // Start monitoring for map disappearance after validation passes
                                let monitorCount = 0;
                                const maxMonitorChecks = 20; // Monitor for 10 seconds
                                const monitorInterval = 500;

                                const monitorMapState = () => {
                                    monitorCount++;
                                    const container = document.getElementById('map-container');
                                    const canvas = container?.querySelector('canvas');
                                    const svg = container?.querySelector('svg');

                                    console.log(`🔍 MONITOR CHECK ${monitorCount}/${maxMonitorChecks} (Small Date Change):`, {
                                        timestamp: new Date().toISOString(),
                                        containerExists: !!container,
                                        canvasExists: !!canvas,
                                        svgExists: !!svg,
                                        canvasSize: canvas ? `${canvas.width}x${canvas.height}` : 'none',
                                        containerChildren: container?.children.length || 0
                                    });

                                    if (!canvas && !svg && container) {
                                        console.error(`❌ MONITOR DETECTED MAP DISAPPEARANCE at check ${monitorCount}! (Small Date Change)`);
                                        setError("Star map disappeared after date change - monitoring detected issue...");
                                        setIsLoading(false);
                                        return;
                                    }

                                    if (monitorCount < maxMonitorChecks) {
                                        setTimeout(monitorMapState, monitorInterval);
                                    } else {
                                        console.log('✅ MONITORING COMPLETED - Map remained stable after small date change');
                                    }
                                };

                                // Start monitoring in 500ms
                                setTimeout(monitorMapState, monitorInterval);
                            }, 500);

                            return;
                        }

                        // If location also changed, perform a full update
                        let latitude, longitude;

                        if (typeof location === 'string') {
                            const coords = location.split(',');
                            latitude = parseFloat(coords[0]);
                            longitude = parseFloat(coords[1]);
                        } else {
                            latitude = parseFloat(location.latitude);
                            longitude = parseFloat(location.longitude);
                        }

                        const basicConfig = {
                            width: mapContainerRef.current?.clientWidth || 600,
                            height: mapContainerRef.current?.clientWidth || 600,
                            container: "map-container",
                            datapath: `${window.location.origin}/d3-celestial-data/`,
                            projection: "stereographic",
                            background: {
                                fill: "transparent",
                                stroke: "transparent",
                                opacity: 0,
                                width: 0
                            },
                            stars: {
                                show: showStars,
                                limit: 6,
                                colors: true,
                                style: { fill: "#ffffff", opacity: 0.8 },
                                designation: false,
                                propername: false
                            },
                            dsos: {
                                show: showDeepSkyObjects,
                                limit: 6,
                                colors: true,
                                style: { fill: "#cccccc", opacity: 0.7 },
                                names: false
                            },
                            constellations: {
                                show: showConstellations,
                                names: showConstellationNames,
                                lines: showConstellations,
                                linestyle: { stroke: "#cccccc", width: 1, opacity: 0.7 },
                                namestyle: { fill: "#cccccc", font: "11px Arial", opacity: 0.8 }
                            },
                            lines: {
                                graticule: { show: showGrid, stroke: "#8989b3", width: 1, opacity: 0.6 },
                                equatorial: { show: showGrid, stroke: "#8989b3", width: 1, opacity: 0.6 },
                                ecliptic: { show: showEcliptic, stroke: "#dda74f", width: 1.5, opacity: 0.7 }
                            },
                            planets: {
                                show: true
                            },
                            center: [0, 0],
                            follow: "zenith",
                            geopos: [latitude, longitude],
                            date: newDateObj
                        };

                        // Force complete rebuild
                        console.log('Using direct Celestial.display call with simple config for date change');
                        window.Celestial.display(basicConfig);

                        // Add proper validation for date+location changes
                        setTimeout(() => {
                            const mapContainer = document.getElementById('map-container');
                            const canvas = mapContainer?.querySelector('canvas');
                            const svg = mapContainer?.querySelector('svg');

                            if (!canvas && !svg) {
                                console.error('❌ Date+location change: No rendering output detected');
                                setError('Date change failed to render - trying alternative approach...');
                                // Clear timeout before fallback
                                if (timeoutRef && timeoutRef.current) {
                                    clearTimeout(timeoutRef.current);
                                }
                                // Fallback to full regeneration
                                initializeStarMap();
                                return;
                            }

                            // Validate canvas content if canvas exists
                            if (canvas) {
                                try {
                                    const ctx = canvas.getContext('2d');
                                    const sampleSize = 50;
                                    const samples = [
                                        [0, 0],
                                        [canvas.width - sampleSize, 0],
                                        [0, canvas.height - sampleSize],
                                        [canvas.width - sampleSize, canvas.height - sampleSize],
                                        [Math.floor(canvas.width / 2) - sampleSize / 2, Math.floor(canvas.height / 2) - sampleSize / 2]
                                    ];

                                    let hasContent = false;
                                    for (const [x, y] of samples) {
                                        const sampleX = Math.max(0, Math.min(x, canvas.width - sampleSize));
                                        const sampleY = Math.max(0, Math.min(y, canvas.height - sampleSize));
                                        const imageData = ctx.getImageData(sampleX, sampleY, sampleSize, sampleSize);

                                        for (let i = 0; i < imageData.data.length; i += 4) {
                                            const r = imageData.data[i];
                                            const g = imageData.data[i + 1];
                                            const b = imageData.data[i + 2];
                                            const a = imageData.data[i + 3];

                                            if ((r > 0 || g > 0 || b > 0) && a > 0) {
                                                hasContent = true;
                                                break;
                                            }
                                        }
                                        if (hasContent) break;
                                    }

                                    if (!hasContent && canvas.width >= 400 && canvas.height >= 400) {
                                        console.log('⚠️ Date+location change: Canvas content detection failed but canvas has proper dimensions, considering as success');
                                        hasContent = true;
                                    }

                                    if (!hasContent) {
                                        console.error('❌ Date+location change: Canvas appears empty');
                                        setError('Date change failed to render - trying alternative approach...');
                                        // Clear timeout before fallback
                                        if (timeoutRef && timeoutRef.current) {
                                            clearTimeout(timeoutRef.current);
                                        }
                                        initializeStarMap();
                                        return;
                                    }
                                } catch (err) {
                                    console.error('❌ Date+location change: Canvas validation error:', err);
                                }
                            }

                            console.log('✅ Date+location change completed successfully');
                            setError(null);
                            notifyMapLoaded(timeoutRef);
                        }, 1000);
                    } catch (err) {
                        console.error('Date change error:', err);
                        setError(`Error with date change: ${err.message}`);
                        // Clear timeout on error
                        if (timeoutRef && timeoutRef.current) {
                            clearTimeout(timeoutRef.current);
                        }
                    }
                }
                // Handle full regeneration or location changes
                else if (regenerate || locationChanged) {
                    // Full rebuild needed for regenerate flag or location change
                    console.log('Doing complete rebuild due to regenerate flag or location change');
                    initializeStarMap();
                }
                // Add back the section for location-only updates
                else if (document.getElementById('map-container') &&
                    document.querySelector('#map-container canvas') &&
                    window.Celestial &&
                    window.Celestial.apply) {
                    // Fast partial update - extract coordinates
                    let latitude, longitude;

                    try {
                        if (typeof location === 'string') {
                            const coords = location.split(',');
                            latitude = parseFloat(coords[0]);
                            longitude = parseFloat(coords[1]);
                        } else {
                            latitude = parseFloat(location.latitude);
                            longitude = parseFloat(location.longitude);
                        }

                        // For location changes only - do fast direct update
                        console.log('Performing fast update for location change only');
                        window.Celestial.apply({
                            geopos: [latitude, longitude],
                            date: new Date(date),
                            // Ensure these settings are passed in location-only changes as well
                            lines: {
                                graticule: { show: showGrid, stroke: "#8989b3", width: 1, opacity: 0.6 },
                                equatorial: { show: showGrid, stroke: "#8989b3", width: 1, opacity: 0.6 },
                                ecliptic: { show: showEcliptic, stroke: "#dda74f", width: 1.5, opacity: 0.7 }
                            }
                        });
                        window.Celestial.redraw();

                        // Add proper validation for location-only changes
                        setTimeout(() => {
                            const mapContainer = document.getElementById('map-container');
                            const canvas = mapContainer?.querySelector('canvas');
                            const svg = mapContainer?.querySelector('svg');

                            if (!canvas && !svg) {
                                console.error('❌ Location-only change: No rendering output detected');
                                setError('Location change failed to render - trying alternative approach...');
                                // Clear timeout before fallback
                                if (timeoutRef && timeoutRef.current) {
                                    clearTimeout(timeoutRef.current);
                                }
                                // Fallback to full regeneration
                                initializeStarMap();
                                return;
                            }

                            // Validate canvas content if canvas exists
                            if (canvas) {
                                try {
                                    const ctx = canvas.getContext('2d');
                                    const sampleSize = 50;
                                    const samples = [
                                        [0, 0],
                                        [canvas.width - sampleSize, 0],
                                        [0, canvas.height - sampleSize],
                                        [canvas.width - sampleSize, canvas.height - sampleSize],
                                        [Math.floor(canvas.width / 2) - sampleSize / 2, Math.floor(canvas.height / 2) - sampleSize / 2]
                                    ];

                                    let hasContent = false;
                                    for (const [x, y] of samples) {
                                        const sampleX = Math.max(0, Math.min(x, canvas.width - sampleSize));
                                        const sampleY = Math.max(0, Math.min(y, canvas.height - sampleSize));
                                        const imageData = ctx.getImageData(sampleX, sampleY, sampleSize, sampleSize);

                                        for (let i = 0; i < imageData.data.length; i += 4) {
                                            const r = imageData.data[i];
                                            const g = imageData.data[i + 1];
                                            const b = imageData.data[i + 2];
                                            const a = imageData.data[i + 3];

                                            if ((r > 0 || g > 0 || b > 0) && a > 0) {
                                                hasContent = true;
                                                break;
                                            }
                                        }
                                        if (hasContent) break;
                                    }

                                    if (!hasContent && canvas.width >= 400 && canvas.height >= 400) {
                                        console.log('⚠️ Location-only change: Canvas content detection failed but canvas has proper dimensions, considering as success');
                                        hasContent = true;
                                    }

                                    if (!hasContent) {
                                        console.error('❌ Location-only change: Canvas appears empty');
                                        setError('Location change failed to render - trying alternative approach...');
                                        // Clear timeout before fallback
                                        if (timeoutRef && timeoutRef.current) {
                                            clearTimeout(timeoutRef.current);
                                        }
                                        initializeStarMap();
                                        return;
                                    }
                                } catch (err) {
                                    console.error('❌ Location-only change: Canvas validation error:', err);
                                }
                            }

                            console.log('✅ Location-only change completed successfully');
                            setError(null);
                            notifyMapLoaded(timeoutRef);
                        }, 300);
                    } catch (e) {
                        console.error('Fast update failed, falling back to full rebuild:', e);
                        // Clear timeout on error
                        if (timeoutRef && timeoutRef.current) {
                            clearTimeout(timeoutRef.current);
                        }
                        initializeStarMap();
                    }
                } else {
                    // Canvas not available or other issues - do full rebuild
                    initializeStarMap();
                }
            } else if (hasInitializedRef.current) {
                console.log('⏸️ Map already initialized successfully, skipping update');
            }
        }
    }, [date, location, size, regenerate, initializeStarMap, showStars, showConstellations, showConstellationNames, showGrid, showDeepSkyObjects, showEcliptic]); // Removed notifyMapLoaded to prevent circular dependency

    // Check periodically for celestial to be loaded
    useEffect(() => {
        if (!isCelestialReady() && celestialAttempts < 25) { // Increased from 15 to 25
            const timer = setTimeout(() => {
                setCelestialAttempts(prev => prev + 1);

                if (isCelestialReady()) {
                    console.log(`Celestial library detected on attempt ${celestialAttempts + 1}`);
                    initializeStarMap();
                } else if (celestialAttempts >= 10) { // Increased threshold before showing message
                    // After a few attempts, use increasing delays
                    console.log(`Celestial library not found on attempt ${celestialAttempts + 1}, will retry...`);
                    if (celestialAttempts === 20) { // Increased threshold before showing error
                        setError('Still loading high-quality star map. Please wait...');
                    }
                }
            }, 300 + celestialAttempts * 150); // Slightly reduced base delay but increased multiplier

            return () => clearTimeout(timer);
        }
    }, [celestialAttempts]); // Removed initializeStarMap dependency to prevent re-runs

    // We need a direct effect to initialize the map on first render
    useEffect(() => {
        console.log('Initial useEffect for map generation');
        // Prevent multiple initialization attempts
        if (isInitializingRef.current || hasInitializedRef.current) {
            console.log('⏸️ Skipping initial effect - already initialized or in progress');
            return;
        }

        // Try to initialize the map if Celestial is ready
        if (isCelestialReady()) {
            console.log('Celestial is ready on initial render, generating map');
            initializeStarMap();
        }
    }, []); // EMPTY dependency array - only run once on mount

    // Set error and notify parent
    useEffect(() => {
        if (error && typeof onError === 'function') {
            onError(error);
        }
    }, [error, onError]);

    // Call notifyMapLoaded when map is successfully initialized
    useEffect(() => {
        if (isCelestialReady() && mapContainerRef.current?.querySelector('canvas') && !error) {
            const timer = setTimeout(() => {
                notifyMapLoaded();
            }, 300); // Small delay to ensure map is fully rendered
            return () => clearTimeout(timer);
        }
    }, [error, notifyMapLoaded]); // Added notifyMapLoaded dependency

    // Fallback timeout to ensure loading indicator disappears
    useEffect(() => {
        const fallbackTimer = setTimeout(() => {
            console.log('Fallback timeout: forcing loading completion');
            notifyMapLoaded();
        }, 5000); // 5 second fallback timeout

        return () => clearTimeout(fallbackTimer);
    }, [date, location, notifyMapLoaded]); // Reset timeout when date or location changes



    // NEW: Function to extract clean SVG from d3-celestial using XMLSerializer method
    const extractStarMapSVG = useCallback(async (maxRetries = 3, retryDelay = 500) => {
        try {
            console.log('🎯 Starting high-quality SVG extraction from d3-celestial using XMLSerializer');

            let svgElement = null;
            let retryCount = 0;

            const extractSVGFromCelestial = () => {
                try {
                    // METHOD 1: Look for existing SVG in the container (d3-celestial sometimes creates SVG)
                    const existingSVG = document.querySelector('#map-container svg');
                    if (existingSVG && isValidStarMapSVG(existingSVG)) {
                        console.log('✅ Found existing SVG element from d3-celestial');
                        return createHighQualitySVG(existingSVG);
                    }

                    // METHOD 2: Try using d3-celestial's built-in export if available
                    if (window.Celestial && typeof window.Celestial.export === 'function') {
                        console.log('🎯 Attempting d3-celestial built-in SVG export...');
                        try {
                            const exportedSVG = window.Celestial.export('svg');
                            if (exportedSVG) {
                                console.log('✅ Successfully exported SVG using Celestial.export');
                                // Parse the SVG data if it's a string
                                if (typeof exportedSVG === 'string') {
                                    const parser = new DOMParser();
                                    const svgDoc = parser.parseFromString(exportedSVG, 'image/svg+xml');
                                    return createHighQualitySVG(svgDoc.documentElement);
                                } else {
                                    return createHighQualitySVG(exportedSVG);
                                }
                            }
                        } catch (exportError) {
                            console.log('⚠️ Celestial.export not available or failed:', exportError.message);
                        }
                    }

                    // METHOD 3: Check if there's canvas content we can convert with maximum quality
                    const mapContainer = document.querySelector('#map-container');
                    if (mapContainer) {
                        const canvas = mapContainer.querySelector('canvas');
                        if (canvas) {
                            console.log('🎨 Found canvas - creating ultra-high-quality SVG with embedded image');
                            return createSVGFromCanvasUltraQuality(canvas);
                        }
                    }

                    console.log('❌ No suitable content found for SVG extraction');
                    return null;

                } catch (error) {
                    console.error('Error during SVG extraction attempt:', error);
                    return null;
                }
            };

            // Helper function to create high-quality SVG using XMLSerializer and stylesheet application
            const createHighQualitySVG = (sourceSVG) => {
                try {
                    // Clone the SVG to avoid modifying the original
                    const clonedSVG = sourceSVG.cloneNode(true);

                    // Apply all computed styles to make the SVG standalone (svg-crowbar method)
                    applyComputedStylesToSVG(clonedSVG);

                    // Ensure proper SVG attributes for standalone use
                    clonedSVG.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
                    clonedSVG.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
                    clonedSVG.setAttribute('version', '1.1');

                    console.log('✅ Created high-quality standalone SVG with applied styles');
                    return clonedSVG;
                } catch (error) {
                    console.error('Error creating high-quality SVG:', error);
                    return sourceSVG; // Return original if styling fails
                }
            };

            // Helper function to apply computed styles to SVG elements (like svg-crowbar does)
            const applyComputedStylesToSVG = (svgElement) => {
                try {
                    // Create empty SVG to get default styles
                    const emptySVG = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                    document.body.appendChild(emptySVG);
                    const emptySvgStyles = window.getComputedStyle(emptySVG);
                    document.body.removeChild(emptySVG);

                    // Apply styles to all elements
                    const allElements = [svgElement, ...svgElement.querySelectorAll('*')];

                    allElements.forEach(element => {
                        const computedStyles = window.getComputedStyle(element);
                        let styleString = '';

                        for (let i = 0; i < computedStyles.length; i++) {
                            const property = computedStyles[i];
                            const value = computedStyles.getPropertyValue(property);
                            const defaultValue = emptySvgStyles.getPropertyValue(property);

                            // Only include styles that differ from defaults
                            if (value !== defaultValue) {
                                styleString += `${property}:${value};`;
                            }
                        }

                        if (styleString) {
                            element.setAttribute('style', styleString);
                        }
                    });

                    console.log('✅ Applied computed styles to SVG for standalone rendering');
                } catch (error) {
                    console.error('Error applying styles to SVG:', error);
                }
            };

            // Helper function to create ultra-high-quality SVG from canvas
            const createSVGFromCanvasUltraQuality = (canvas) => {
                try {
                    const rect = canvas.getBoundingClientRect();
                    const width = canvas.width || rect.width || 800;
                    const height = canvas.height || rect.height || 800;

                    // Create SVG with high quality settings
                    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                    svg.setAttribute('width', width);
                    svg.setAttribute('height', height);
                    svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
                    svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
                    svg.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
                    svg.setAttribute('version', '1.1');

                    // Convert canvas to ultra-high-quality data URL
                    const imageDataURL = canvas.toDataURL('image/png', 1.0); // Maximum quality

                    // Create image element in SVG with print-ready attributes
                    const image = document.createElementNS('http://www.w3.org/2000/svg', 'image');
                    image.setAttribute('href', imageDataURL);
                    image.setAttribute('x', '0');
                    image.setAttribute('y', '0');
                    image.setAttribute('width', width);
                    image.setAttribute('height', height);
                    image.setAttribute('image-rendering', 'optimizeQuality');
                    image.setAttribute('shape-rendering', 'geometricPrecision');
                    image.setAttribute('text-rendering', 'geometricPrecision');
                    image.setAttribute('color-rendering', 'optimizeQuality');

                    svg.appendChild(image);

                    console.log('✅ Created ultra-high-quality SVG from canvas:', {
                        width: width,
                        height: height,
                        imageSize: Math.round(imageDataURL.length / 1024) + 'KB',
                        quality: 'MAXIMUM (100%)'
                    });

                    return svg;
                } catch (error) {
                    console.error('Error creating SVG from canvas:', error);
                    return null;
                }
            };

            // Helper function to validate if this is actually a star map SVG
            const isValidStarMapSVG = (svg) => {
                if (!svg || svg.tagName.toLowerCase() !== 'svg') return false;

                // Check for d3-celestial specific content
                const hasStarMapContent =
                    svg.children.length > 5 || // Should have multiple child elements
                    svg.querySelector('circle') || // Should have stars (circles)
                    svg.querySelector('path[class*="star"]') || // Star paths
                    svg.querySelector('path[class*="const"]') || // Constellation lines
                    svg.querySelector('g[class*="celestial"]') || // Celestial groups
                    svg.querySelector('[class*="graticule"]') || // Grid lines
                    svg.getAttribute('class')?.includes('celestial') ||
                    svg.getAttribute('id')?.includes('celestial');

                // Also check SVG dimensions - star map should be substantial
                const rect = svg.getBoundingClientRect();
                const hasValidDimensions = rect.width > 50 && rect.height > 50;

                const isValid = hasStarMapContent && hasValidDimensions;

                if (!isValid) {
                    console.log(`❌ SVG validation failed for selector`, {
                        hasContent: hasStarMapContent,
                        hasDimensions: hasValidDimensions,
                        children: svg.children.length,
                        dimensions: `${rect.width}x${rect.height}`,
                        classList: svg.getAttribute('class'),
                        svgPreview: svg.outerHTML.substring(0, 200) + '...'
                    });
                }

                return isValid;
            };

            // Try to extract SVG immediately
            svgElement = extractSVGFromCelestial();

            if (!svgElement) {
                // Not found immediately, try with retries 
                console.log('🔄 SVG not found immediately, trying with retries...');
                console.log('🔄 This is normal - d3-celestial needs time to render');

                while (retryCount < maxRetries && !svgElement) {
                    retryCount++;
                    const currentDelay = retryDelay * retryCount;
                    console.log(`⏳ Retry ${retryCount}/${maxRetries} - waiting ${currentDelay}ms for d3-celestial...`);

                    await new Promise(resolve => setTimeout(resolve, currentDelay));
                    svgElement = extractSVGFromCelestial();

                    if (svgElement) {
                        console.log(`✅ Found SVG on retry ${retryCount}!`);
                        break;
                    } else {
                        console.log(`❌ Retry ${retryCount} failed - still no SVG found`);
                    }
                }
            }

            if (!svgElement) {
                console.error('❌ No SVG could be extracted from d3-celestial after', maxRetries, 'retries');

                // Enhanced debugging
                const mapContainer = document.querySelector('#map-container');
                console.log('🔍 Debug: Container state:', {
                    'map-container exists': !!mapContainer,
                    'map-container children': mapContainer?.children.length || 0,
                    'any canvas count': document.querySelectorAll('#map-container canvas').length,
                    'celestial ready': !!window.Celestial,
                    'd3 available': !!window.d3,
                    'celestial export available': !!window.Celestial?.export
                });

                // Final fallback: try direct canvas conversion
                const canvas = document.querySelector('#map-container canvas');
                if (canvas) {
                    console.log('⚠️ FALLBACK: Converting canvas to SVG wrapper (reduced quality)');
                    return await convertCanvasToSVG(canvas);
                }

                return null;
            }

            console.log('✅ HIGH QUALITY: SVG extracted successfully:', {
                width: svgElement.getAttribute('width'),
                height: svgElement.getAttribute('height'),
                children: svgElement.children.length,
                tagName: svgElement.tagName,
                isVectorBased: true,
                hasImageData: !!svgElement.querySelector('image[href*="data:image"]')
            });

            // Clone the SVG to avoid modifying the original
            const clonedSVG = svgElement.cloneNode(true);

            // Clean up and optimize the SVG for jsPDF
            cleanSVGForJsPDF(clonedSVG);

            // Set proper namespaces and attributes for jsPDF compatibility
            clonedSVG.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
            clonedSVG.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');

            // Ensure the SVG has proper dimensions
            const svgRect = svgElement.getBoundingClientRect();
            if (!clonedSVG.getAttribute('width') || !clonedSVG.getAttribute('height')) {
                clonedSVG.setAttribute('width', svgRect.width || '800');
                clonedSVG.setAttribute('height', svgRect.height || '800');
            }

            // Set viewBox if not present and normalize coordinate system
            if (!clonedSVG.getAttribute('viewBox')) {
                const width = clonedSVG.getAttribute('width');
                const height = clonedSVG.getAttribute('height');
                clonedSVG.setAttribute('viewBox', `0 0 ${width} ${height}`);
            }

            // Add circular clipping to the extracted star map to prevent overflow during export
            // This ensures content is constrained within circular bounds even at the source level
            const svgDefs = clonedSVG.querySelector('defs') || document.createElementNS('http://www.w3.org/2000/svg', 'defs');
            if (!clonedSVG.querySelector('defs')) {
                clonedSVG.insertBefore(svgDefs, clonedSVG.firstChild);
            }

            // Add a circular clip path at the SVG level
            const existingClip = svgDefs.querySelector('#starMapCircularClip');
            if (!existingClip) {
                const svgWidth = parseFloat(clonedSVG.getAttribute('width')) || 800;
                const svgHeight = parseFloat(clonedSVG.getAttribute('height')) || 800;
                const radius = Math.min(svgWidth, svgHeight) / 2;
                const centerX = svgWidth / 2;
                const centerY = svgHeight / 2;

                const clipPath = document.createElementNS('http://www.w3.org/2000/svg', 'clipPath');
                clipPath.setAttribute('id', 'starMapCircularClip');
                const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
                circle.setAttribute('cx', centerX);
                circle.setAttribute('cy', centerY);
                circle.setAttribute('r', radius);
                clipPath.appendChild(circle);
                svgDefs.appendChild(clipPath);

                // Apply the clipping to all content groups in the star map
                const contentGroups = clonedSVG.querySelectorAll('g');
                contentGroups.forEach(group => {
                    // Only apply clipping to groups that don't already have it
                    if (!group.getAttribute('clip-path')) {
                        group.setAttribute('clip-path', 'url(#starMapCircularClip)');
                    }
                });

                console.log('✅ Applied circular clipping to extracted star map SVG');
            }

            // Count the elements in the SVG for debugging
            const elementCounts = countSVGElements(clonedSVG);
            console.log('SVG extraction successful:', {
                totalElements: elementCounts.total,
                stars: elementCounts.stars,
                paths: elementCounts.paths,
                text: elementCounts.text,
                groups: elementCounts.groups
            });

            return clonedSVG;

        } catch (error) {
            console.error('Error extracting SVG:', error);
            return null;
        }
    }, []); // Empty dependency array since this function doesn't depend on any changing values

    // Helper function to clean SVG for jsPDF compatibility
    const cleanSVGForJsPDF = (svgElement) => {
        try {
            // Remove any script elements for security
            const scripts = svgElement.querySelectorAll('script');
            scripts.forEach(script => script.remove());

            // Remove any foreign objects that might cause issues
            const foreignObjects = svgElement.querySelectorAll('foreignObject');
            foreignObjects.forEach(obj => obj.remove());

            // Clean up any inline styles that might conflict
            const allElements = svgElement.querySelectorAll('*');
            allElements.forEach(element => {
                // Remove event handlers
                const attributes = [...element.attributes];
                attributes.forEach(attr => {
                    if (attr.name.startsWith('on')) {
                        element.removeAttribute(attr.name);
                    }
                });

                // Normalize class names for consistency
                if (element.className && typeof element.className.baseVal === 'string') {
                    element.setAttribute('class', element.className.baseVal);
                }
            });

            // Fix font issues for jsPDF compatibility
            const normalizeFont = (fontFamily, fontWeight, fontStyle) => {
                // Map of font families to jsPDF compatible names
                const fontMap = {
                    'helvetica': 'helvetica',
                    'arial': 'helvetica',
                    'sans-serif': 'helvetica',
                    'times': 'times',
                    'times new roman': 'times',
                    'serif': 'times',
                    'courier': 'courier',
                    'courier new': 'courier',
                    'monospace': 'courier'
                };

                // Normalize font family
                let normalizedFamily = 'helvetica'; // default
                if (fontFamily) {
                    const lowerFamily = fontFamily.toLowerCase().replace(/['"]/g, '').split(',')[0].trim();
                    normalizedFamily = fontMap[lowerFamily] || 'helvetica';
                }

                // Normalize font weight and style to jsPDF supported variants
                let normalizedStyle = 'normal';

                // Handle font weights
                if (fontWeight) {
                    const weightValue = parseInt(fontWeight) || fontWeight.toString().toLowerCase();
                    if (weightValue === 'bold' || weightValue >= 600) {
                        normalizedStyle = 'bold';
                    } else if (weightValue === 'normal' || weightValue <= 400) {
                        normalizedStyle = 'normal';
                    }
                }

                // Handle font styles
                if (fontStyle) {
                    const styleValue = fontStyle.toString().toLowerCase();
                    if (styleValue === 'italic') {
                        normalizedStyle = normalizedStyle === 'bold' ? 'bolditalic' : 'italic';
                    }
                }

                return { family: normalizedFamily, style: normalizedStyle };
            };

            // Process all text elements to fix font issues
            const textElements = svgElement.querySelectorAll('text, tspan');
            textElements.forEach(textElement => {
                // Get current font properties
                const computedStyle = window.getComputedStyle(textElement);
                const fontFamily = textElement.getAttribute('font-family') || computedStyle.fontFamily;
                const fontWeight = textElement.getAttribute('font-weight') || computedStyle.fontWeight;
                const fontStyle = textElement.getAttribute('font-style') || computedStyle.fontStyle;

                // Normalize fonts for jsPDF
                const normalized = normalizeFont(fontFamily, fontWeight, fontStyle);

                // Apply normalized font attributes
                textElement.setAttribute('font-family', normalized.family);
                textElement.setAttribute('font-weight', normalized.style.includes('bold') ? 'bold' : 'normal');
                textElement.setAttribute('font-style', normalized.style.includes('italic') ? 'italic' : 'normal');

                // Remove any problematic style attributes
                textElement.removeAttribute('font-variant');
                textElement.removeAttribute('font-stretch');

                // Clean up inline styles that might contain problematic fonts
                const style = textElement.getAttribute('style');
                if (style) {
                    const cleanedStyle = style
                        .replace(/font-family\s*:\s*[^;]*/gi, `font-family: ${normalized.family}`)
                        .replace(/font-weight\s*:\s*[^;]*/gi, `font-weight: ${normalized.style.includes('bold') ? 'bold' : 'normal'}`)
                        .replace(/font-style\s*:\s*[^;]*/gi, `font-style: ${normalized.style.includes('italic') ? 'italic' : 'normal'}`)
                        .replace(/font-variant\s*:\s*[^;]*/gi, '')
                        .replace(/font-stretch\s*:\s*[^;]*/gi, '');
                    textElement.setAttribute('style', cleanedStyle);
                }
            });

            // Remove any empty groups that might cause rendering issues
            const emptyGroups = svgElement.querySelectorAll('g:empty');
            emptyGroups.forEach(group => group.remove());

            // Ensure all paths have proper stroke and fill attributes
            const paths = svgElement.querySelectorAll('path');
            paths.forEach(path => {
                if (!path.getAttribute('fill') && !path.style.fill) {
                    path.setAttribute('fill', 'none');
                }
            });

            // Ensure all circles (stars) have proper attributes
            const circles = svgElement.querySelectorAll('circle');
            circles.forEach(circle => {
                if (!circle.getAttribute('fill') && !circle.style.fill) {
                    circle.setAttribute('fill', '#ffffff');
                }
            });

            console.log('✅ SVG cleaned for jsPDF compatibility with font normalization');

        } catch (error) {
            console.error('❌ Error cleaning SVG:', error);
        }
    };

    // Helper function to count SVG elements for debugging
    const countSVGElements = (svgElement) => {
        return {
            total: svgElement.querySelectorAll('*').length,
            stars: svgElement.querySelectorAll('circle.star, circle[class*="star"]').length,
            paths: svgElement.querySelectorAll('path').length,
            text: svgElement.querySelectorAll('text').length,
            groups: svgElement.querySelectorAll('g').length,
            lines: svgElement.querySelectorAll('line').length,
            constellations: svgElement.querySelectorAll('path.constline, path[class*="constline"]').length,
            grid: svgElement.querySelectorAll('path.graticule, path.gridline, path.equatorial').length,
            ecliptic: svgElement.querySelectorAll('path.ecliptic').length
        };
    };

    // Helper function to get SVG as string for debugging
    const getSVGString = (svgElement) => {
        if (!svgElement) return null;

        try {
            const serializer = new XMLSerializer();
            const svgString = serializer.serializeToString(svgElement);
            return svgString;
        } catch (error) {
            console.error('Error serializing SVG:', error);
            return null;
        }
    };

    // Helper function to convert canvas to SVG when d3-celestial uses canvas rendering
    const convertCanvasToSVG = async (canvas) => {
        try {
            console.log('🔄 Converting canvas to high-quality SVG...');

            // Get canvas dimensions
            const rect = canvas.getBoundingClientRect();
            const baseWidth = canvas.width || rect.width || 800;
            const baseHeight = canvas.height || rect.height || 800;

            // Use higher resolution for better quality (2x scaling)
            const scaleFactor = 2;
            const width = baseWidth * scaleFactor;
            const height = baseHeight * scaleFactor;

            // Create high-resolution canvas for better quality
            const highResCanvas = document.createElement('canvas');
            highResCanvas.width = width;
            highResCanvas.height = height;
            const ctx = highResCanvas.getContext('2d');

            // Enable high-quality scaling
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';

            // Draw original canvas scaled up
            ctx.drawImage(canvas, 0, 0, width, height);

            // Create SVG element with proper dimensions
            const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            svg.setAttribute('width', baseWidth);
            svg.setAttribute('height', baseHeight);
            svg.setAttribute('viewBox', `0 0 ${baseWidth} ${baseHeight}`);
            svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
            svg.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');

            // Convert high-res canvas to image data with maximum quality
            const imageData = highResCanvas.toDataURL('image/png', 1.0); // 100% quality

            // Create image element in SVG
            const image = document.createElementNS('http://www.w3.org/2000/svg', 'image');
            image.setAttribute('x', '0');
            image.setAttribute('y', '0');
            image.setAttribute('width', baseWidth);
            image.setAttribute('height', baseHeight);
            image.setAttribute('href', imageData);

            // Add image smoothing for crisp rendering
            image.setAttribute('image-rendering', 'optimizeQuality');
            image.setAttribute('shape-rendering', 'geometricPrecision');

            // Add image to SVG
            svg.appendChild(image);

            console.log('✅ Canvas converted to high-quality SVG successfully:', {
                originalDimensions: `${baseWidth}x${baseHeight}`,
                highResDimensions: `${width}x${height}`,
                scaleFactor: scaleFactor,
                imageDataLength: imageData.length,
                quality: 'high'
            });

            return svg;

        } catch (error) {
            console.error('❌ Error converting canvas to SVG:', error);
            return null;
        }
    };



    // NEW: Extract complete map preview (needed first for PDF generation)
    const extractCompleteMapPreview = useCallback(async (backgroundType = null) => {
        try {
            console.log('🎨 Starting complete map preview extraction...');

            if (backgroundType) {
                console.log('🎨 Background type provided directly:', backgroundType);
            }

            // Function to get background style directly from background type
            const getBackgroundFromType = (bgType) => {
                if (!bgType) return null;

                // Map background types to image files (same as MapPreview.js)
                const backgrounds = {
                    // Classic Backgrounds (Free) - now using PNG files
                    'gray': '/images/product-gradients/classic-gray.png',
                    'black': '/images/product-gradients/classic-black.png',

                    // Basic Harmony
                    'basic-light': '/images/product-gradients/harmony-1.png',
                    'basic-dark': '/images/product-gradients/harmony-2.png',

                    // Sunset Vibe
                    'sunset-pink': '/images/product-gradients/sunset-1.png',
                    'sunset-orange': '/images/product-gradients/sunset-2.png',

                    // Pastel Palette
                    'pastel-pink': '/images/product-gradients/pastel-1.png',
                    'pastel-blue': '/images/product-gradients/pastel-2.png',
                    'pastel-white': '/images/product-gradients/pastel-3.png',

                    // Neon Glow
                    'neon-purple': '/images/product-gradients/neon-1.png',
                    'neon-blue': '/images/product-gradients/neon-2.png',

                    // Natural Tones
                    'natural-gold': '/images/product-gradients/natural-1.png',
                    'natural-brown': '/images/product-gradients/natural-2.png',
                    'natural-beige': '/images/product-gradients/natural-3.png',

                    // Golden Elegance
                    'golden-dark': '/images/product-gradients/golden-elegance-1.png',
                    'golden-black': '/images/product-gradients/golden-elegance-2.png',
                    'golden-gold': '/images/product-gradients/golden-elegance-3.png'
                };

                const bgValue = backgrounds[bgType];
                if (!bgValue) return null;

                // Check if it's a color or an image path
                if (bgValue.startsWith('#')) {
                    return { type: 'color', value: bgValue };
                } else {
                    // Convert relative path to absolute URL
                    const imageUrl = window.location.origin + bgValue;
                    return { type: 'image', value: imageUrl };
                }
            };

            // Find the main preview container
            const previewContainer = document.querySelector('.poster-bg-container');
            if (!previewContainer) {
                throw new Error('Preview container not found');
            }

            // Get dimensions from the container
            const containerRect = previewContainer.getBoundingClientRect();
            const width = Math.round(containerRect.width) || 600;
            const height = Math.round(containerRect.height) || 750;

            console.log('🎨 Preview dimensions:', { width, height });

            // Create the main SVG container
            const svgNS = "http://www.w3.org/2000/svg";
            const mainSvg = document.createElementNS(svgNS, "svg");
            mainSvg.setAttribute("width", width);
            mainSvg.setAttribute("height", height);
            mainSvg.setAttribute("viewBox", `0 0 ${width} ${height}`);
            mainSvg.setAttribute("xmlns", svgNS);

            // Add poster background - this will be replaced with the selected background
            const posterBackground = document.createElementNS(svgNS, "rect");
            posterBackground.setAttribute("width", "100%");
            posterBackground.setAttribute("height", "100%");
            posterBackground.setAttribute("fill", "white"); // Default to white, will be changed if needed
            mainSvg.appendChild(posterBackground);

            // Enhanced background detection - try direct background type first, then DOM fallback
            console.log('🔍 Starting enhanced background detection...');
            console.log('🎯 Input backgroundType:', backgroundType);

            // Priority 1: Use direct background type if provided
            let directBackground = null;
            if (backgroundType) {
                directBackground = getBackgroundFromType(backgroundType);
                console.log('🎯 Direct background detection result:', directBackground);
                console.log('🎯 Background type mapping for:', backgroundType, '→', directBackground);
            } else {
                console.log('⚠️ No backgroundType provided, will try DOM detection');
            }

            // First, try to get the background style from React state/props
            // Look for the actual background element that should have the style applied
            const posterContainer = document.querySelector('.poster-bg-container');

            if (posterContainer) {
                // Try to find data attributes or inspect the applied styles
                const computedStyle = window.getComputedStyle(posterContainer);
                console.log('🎨 Poster container style check:', {
                    background: computedStyle.background,
                    backgroundImage: computedStyle.backgroundImage
                });
            }

            // Try multiple possible background containers
            const backgroundSelectors = [
                '.background-container',
                '.poster-bg-container .background-container',
                '.poster-bg-container',
                '[style*="background"]',
                '[class*="background"]'
            ];

            let backgroundContainer = null;
            let backgroundFound = false;

            // Process direct background first (highest priority)
            if (directBackground) {
                console.log('🎯 Processing direct background:', directBackground);

                if (directBackground.type === 'image') {
                    try {
                        console.log('🖼️ Creating background image element with URL:', directBackground.value);

                        const bgImageElement = document.createElementNS(svgNS, "image");
                        bgImageElement.setAttribute("x", "0");
                        bgImageElement.setAttribute("y", "0");
                        bgImageElement.setAttribute("width", width);
                        bgImageElement.setAttribute("height", height);
                        bgImageElement.setAttribute("href", directBackground.value);
                        bgImageElement.setAttribute("preserveAspectRatio", "xMidYMid slice");

                        // Add the background image right after the white background
                        mainSvg.insertBefore(bgImageElement, mainSvg.firstChild.nextSibling);
                        backgroundFound = true;

                        console.log('✅ Direct background image applied successfully:');
                        console.log('  - URL:', directBackground.value);
                        console.log('  - Dimensions:', `${width}x${height}`);
                        console.log('  - Position: inserted after white background');

                        // Test if the image URL is accessible
                        const testImg = new Image();
                        testImg.onload = () => console.log('🎯 Background image loads successfully:', directBackground.value);
                        testImg.onerror = () => console.error('❌ Background image failed to load:', directBackground.value);
                        testImg.src = directBackground.value;

                    } catch (bgError) {
                        console.error('❌ Failed to apply direct background image:', bgError);
                        console.error('Background details:', directBackground);
                    }
                } else if (directBackground.type === 'color') {
                    // For solid colors, replace the poster background with the selected color
                    // This ensures the entire poster (not just the background container) has the correct color
                    posterBackground.setAttribute("fill", directBackground.value);

                    backgroundFound = true;
                    console.log('✅ Direct background color applied to poster:', directBackground.value);
                }
            } else {
                console.log('⚠️ No direct background to process');
            }

            // If direct background failed or wasn't provided, try DOM detection
            if (!backgroundFound) {
                console.log('🔍 Direct background not available, trying DOM detection...');

                for (const selector of backgroundSelectors) {
                    backgroundContainer = document.querySelector(selector);
                    if (backgroundContainer) {
                        const bgStyle = window.getComputedStyle(backgroundContainer);

                        console.log(`🎨 Checking selector "${selector}":`, {
                            element: backgroundContainer.className,
                            backgroundImage: bgStyle.backgroundImage,
                            background: bgStyle.background,
                            backgroundColor: bgStyle.backgroundColor,
                            styleAttribute: backgroundContainer.style.cssText
                        });

                        // Check if this element has any background
                        if (bgStyle.backgroundImage !== 'none' ||
                            bgStyle.background.includes('url(') ||
                            (bgStyle.backgroundColor && bgStyle.backgroundColor !== 'rgba(0, 0, 0, 0)')) {

                            console.log(`✅ Found background on selector: "${selector}"`);
                            break;
                        }
                    }
                }

                if (backgroundContainer) {
                    const bgStyle = window.getComputedStyle(backgroundContainer);
                    const bgImage = bgStyle.backgroundImage;
                    const background_property = bgStyle.background;

                    console.log('🎨 Final background analysis:', {
                        selector: backgroundContainer.className,
                        backgroundImage: bgImage,
                        background: background_property,
                        backgroundColor: bgStyle.backgroundColor,
                        inlineStyle: backgroundContainer.style.cssText
                    });

                    // Method 1: Direct background-image property
                    if (bgImage && bgImage !== 'none') {
                        console.log('🖼️ Processing background-image...');

                        try {
                            const urlMatch = bgImage.match(/url\("?([^"')]+)"?\)/);
                            if (urlMatch && urlMatch[1]) {
                                let imageUrl = urlMatch[1];

                                // Convert relative URLs to absolute
                                if (imageUrl.startsWith('/')) {
                                    imageUrl = window.location.origin + imageUrl;
                                }

                                const bgImageElement = document.createElementNS(svgNS, "image");
                                bgImageElement.setAttribute("x", "0");
                                bgImageElement.setAttribute("y", "0");
                                bgImageElement.setAttribute("width", width);
                                bgImageElement.setAttribute("height", height);
                                bgImageElement.setAttribute("href", imageUrl);
                                bgImageElement.setAttribute("preserveAspectRatio", "xMidYMid slice");
                                mainSvg.appendChild(bgImageElement);
                                backgroundFound = true;
                                console.log('✅ Background image added from backgroundImage:', imageUrl);
                            }
                        } catch (bgError) {
                            console.warn('Could not process background-image:', bgError);
                        }
                    }

                    // Method 2: URL in background property (most common for gradients)
                    if (!backgroundFound && background_property && background_property.includes('url(')) {
                        console.log('🖼️ Processing gradient background from background property...');

                        try {
                            const urlMatch = background_property.match(/url\("?([^"')]+)"?\)/);
                            if (urlMatch && urlMatch[1]) {
                                let imageUrl = urlMatch[1];

                                // Convert relative URLs to absolute
                                if (imageUrl.startsWith('/')) {
                                    imageUrl = window.location.origin + imageUrl;
                                }

                                console.log('🔗 Converting background URL:', {
                                    original: urlMatch[1],
                                    absolute: imageUrl
                                });

                                const bgImageElement = document.createElementNS(svgNS, "image");
                                bgImageElement.setAttribute("x", "0");
                                bgImageElement.setAttribute("y", "0");
                                bgImageElement.setAttribute("width", width);
                                bgImageElement.setAttribute("height", height);
                                bgImageElement.setAttribute("href", imageUrl);
                                bgImageElement.setAttribute("preserveAspectRatio", "xMidYMid slice");
                                mainSvg.appendChild(bgImageElement);
                                backgroundFound = true;
                                console.log('✅ Gradient background image added:', imageUrl);
                            }
                        } catch (bgError) {
                            console.warn('Could not process gradient background:', bgError);
                        }
                    }

                    // Method 3: Solid color fallback
                    if (!backgroundFound) {
                        console.log('🎨 Processing solid color background...');
                        const bgColor = bgStyle.backgroundColor;
                        if (bgColor && bgColor !== 'rgba(0, 0, 0, 0)') {
                            posterBackground.setAttribute("fill", bgColor);
                            backgroundFound = true;
                            console.log('✅ Solid color background applied to poster:', bgColor);
                        } else if (background_property && !background_property.includes('url(')) {
                            // Extract color from background property
                            const colorMatch = background_property.match(/#[0-9a-f]{6}|#[0-9a-f]{3}|rgb\([^)]+\)|rgba\([^)]+\)/i);
                            if (colorMatch) {
                                posterBackground.setAttribute("fill", colorMatch[0]);
                                backgroundFound = true;
                                console.log('✅ Color background applied to poster from background property:', colorMatch[0]);
                            }
                        }
                    }
                }

                // Manual fallback: If no background found, try to detect from DOM or force gradient backgrounds
                if (!backgroundFound) {
                    console.warn('⚠️ No background found via CSS detection, trying manual detection...');

                    // Try to detect the background type from DOM elements or data attributes
                    const backgroundContainerEl = document.querySelector('.background-container');
                    if (backgroundContainerEl) {
                        // Check if the background container has a style attribute that got overridden
                        const inlineStyle = backgroundContainerEl.style.cssText;
                        console.log('🔍 Checking inline style:', inlineStyle);

                        // If we still can't find it, let's manually apply common gradient backgrounds
                        // Look for signs of which background should be applied
                        const formElements = document.querySelectorAll('[data-background], [class*="background-"]');
                        console.log('🔍 Found form elements with background data:', formElements.length);

                        // For now, if we can't detect the background, add a placeholder that shows we tried
                        console.log('🎨 Using fallback: checking for common gradient patterns...');

                        // Try to add a common gradient as fallback (Basic Harmony Light)
                        const fallbackImageUrl = window.location.origin + '/images/product-gradients/harmony-1.png';
                        console.log('🔄 Attempting fallback gradient:', fallbackImageUrl);

                        try {
                            const bgImageElement = document.createElementNS(svgNS, "image");
                            bgImageElement.setAttribute("x", "0");
                            bgImageElement.setAttribute("y", "0");
                            bgImageElement.setAttribute("width", width);
                            bgImageElement.setAttribute("height", height);
                            bgImageElement.setAttribute("href", fallbackImageUrl);
                            bgImageElement.setAttribute("preserveAspectRatio", "xMidYMid slice");
                            mainSvg.appendChild(bgImageElement);
                            backgroundFound = true;
                            console.log('✅ Applied fallback gradient background');
                        } catch (fallbackError) {
                            console.warn('❌ Fallback gradient failed:', fallbackError);
                            posterBackground.setAttribute("fill", "white");
                        }
                    } else {
                        posterBackground.setAttribute("fill", "white");
                    }
                }
            }

            // Extract the star map SVG
            console.log('⭐ Extracting star map...');
            const starMapSvg = await extractStarMapSVG(3, 500);

            if (starMapSvg) {
                // CORRECT APPROACH - Make the circular area itself larger to match preview
                const mapSize = Math.min(width * 0.8, height * 0.6); // Larger circular area like in preview
                const mapX = (width - mapSize) / 2; // Center horizontally
                const mapY = height * 0.10; // Even higher up at 10% from top

                console.log('🟢 CIRCLE SIZE UPDATE', Date.now(), 'mapSize:', mapSize, 'mapY:', mapY);

                // Create defs section with circular clipping path first
                const defs = document.createElementNS(svgNS, "defs");
                const clipPath = document.createElementNS(svgNS, "clipPath");
                clipPath.setAttribute("id", "circularClip");
                const circle = document.createElementNS(svgNS, "circle");
                circle.setAttribute("cx", mapSize / 2);
                circle.setAttribute("cy", mapSize / 2);
                circle.setAttribute("r", mapSize / 2);
                clipPath.appendChild(circle);
                defs.appendChild(clipPath);
                mainSvg.appendChild(defs);

                // Position the star map in the center-top area (matching the preview layout)
                const mapGroup = document.createElementNS(svgNS, "g");
                mapGroup.setAttribute("transform", `translate(${mapX}, ${mapY})`);

                // Clone the star map content and normalize its coordinate system
                const mapContent = starMapSvg.cloneNode(true);

                // Get original star map dimensions
                const originalWidth = parseFloat(starMapSvg.getAttribute("width")) || 800;
                const originalHeight = parseFloat(starMapSvg.getAttribute("height")) || 600;
                const originalViewBox = starMapSvg.getAttribute("viewBox");

                // Create a wrapper group for the circular container
                const circularContainer = document.createElementNS(svgNS, "g");
                circularContainer.setAttribute("clip-path", "url(#circularClip)");

                // Add a circular background for the star map area
                const mapBackground = document.createElementNS(svgNS, "circle");
                mapBackground.setAttribute("cx", mapSize / 2);
                mapBackground.setAttribute("cy", mapSize / 2);
                mapBackground.setAttribute("r", mapSize / 2);
                mapBackground.setAttribute("fill", "#000000");
                circularContainer.appendChild(mapBackground);

                // Create a centering group for the star map content
                const centeringGroup = document.createElementNS(svgNS, "g");

                // PROPER SCALING - fit star map naturally within larger circle to maintain quality
                const padding = 1.0; // Natural 1:1 scale to maintain star map quality
                const scale = (mapSize * padding) / Math.max(originalWidth, originalHeight);

                // Center the star map in the larger circle
                const scaledWidth = originalWidth * scale;
                const scaledHeight = originalHeight * scale;
                const centerX = (mapSize - scaledWidth) / 2;
                const centerY = (mapSize - scaledHeight) / 2; // Perfect center in larger circle

                console.log('🟢 QUALITY SCALE UPDATE', Date.now(), 'padding:', padding, 'scale:', scale, 'mapSize:', mapSize);

                // Apply transform to center and scale the star map
                centeringGroup.setAttribute("transform", `translate(${centerX}, ${centerY}) scale(${scale})`);

                // Set up the star map content with proper dimensions and viewBox
                mapContent.setAttribute("width", originalWidth);
                mapContent.setAttribute("height", originalHeight);

                if (originalViewBox) {
                    mapContent.setAttribute("viewBox", originalViewBox);
                } else {
                    mapContent.setAttribute("viewBox", `0 0 ${originalWidth} ${originalHeight}`);
                }

                // Remove any transform on the original content to avoid conflicts
                mapContent.removeAttribute("transform");
                mapContent.setAttribute("preserveAspectRatio", "xMidYMid meet");

                // Add the star map content to the centering group
                centeringGroup.appendChild(mapContent);

                // Add the centering group to the circular container
                circularContainer.appendChild(centeringGroup);

                // Add the circular container to the main group
                mapGroup.appendChild(circularContainer);
                mainSvg.appendChild(mapGroup);

                console.log('✅ Star map added to composite SVG with proper circular clipping');
            } else {
                console.warn('⚠️ Could not extract star map, adding placeholder');
                // Add a placeholder circle
                const placeholder = document.createElementNS(svgNS, "circle");
                placeholder.setAttribute("cx", width / 2);
                placeholder.setAttribute("cy", height * 0.4);
                placeholder.setAttribute("r", Math.min(width, height) * 0.3);
                placeholder.setAttribute("fill", "#000000");
                placeholder.setAttribute("stroke", "#ffffff");
                placeholder.setAttribute("stroke-width", "2");
                mainSvg.appendChild(placeholder);
            }

            // Extract orbital decorations
            console.log('🎭 Processing orbital decorations...');
            const orbitalWrapper = document.querySelector('.orbital-decoration-wrapper');
            if (orbitalWrapper) {
                const orbitalImg = orbitalWrapper.querySelector('img');
                if (orbitalImg && orbitalImg.src) {
                    try {
                        // Scale up orbital decorations to make them more prominent
                        const decorationScale = 1.05; // Scale up by 5%
                        const scaledWidth = width * decorationScale;
                        const scaledHeight = height * decorationScale;

                        // Center the scaled decorations
                        const offsetX = (width - scaledWidth) / 2;
                        const offsetY = (height - scaledHeight) / 2;

                        // Add orbital decoration as overlay
                        const orbitalElement = document.createElementNS(svgNS, "image");
                        orbitalElement.setAttribute("x", offsetX);
                        orbitalElement.setAttribute("y", offsetY);
                        orbitalElement.setAttribute("width", scaledWidth);
                        orbitalElement.setAttribute("height", scaledHeight);
                        orbitalElement.setAttribute("href", orbitalImg.src);
                        orbitalElement.setAttribute("preserveAspectRatio", "xMidYMid slice");
                        orbitalElement.setAttribute("opacity", "0.9");
                        mainSvg.appendChild(orbitalElement);
                        console.log('✅ Orbital decorations added with 5% scale increase');
                    } catch (orbitalError) {
                        console.warn('Could not add orbital decorations:', orbitalError);
                    }
                }
            }

            // Extract and add text caption
            console.log('📝 Adding text caption...');
            const captionContainer = document.querySelector('.text-center.mt-4.mb-2.text-white\\/80');
            if (captionContainer) {
                const textGroup = document.createElementNS(svgNS, "g");

                // Position text at bottom
                const textY = height - 80;

                // Main caption text
                const titleElement = captionContainer.querySelector('.font-light.text-lg.mb-1');
                if (titleElement) {
                    const titleText = document.createElementNS(svgNS, "text");
                    titleText.setAttribute("x", width / 2);
                    titleText.setAttribute("y", textY);
                    titleText.setAttribute("text-anchor", "middle");
                    titleText.setAttribute("fill", "white");
                    titleText.setAttribute("font-family", "Arial, sans-serif");
                    titleText.setAttribute("font-size", "18");
                    titleText.setAttribute("font-weight", "300");
                    titleText.textContent = titleElement.textContent;
                    textGroup.appendChild(titleText);
                }

                // Location and date text
                const detailsElement = captionContainer.querySelector('.text-xs.opacity-70');
                if (detailsElement) {
                    const lines = detailsElement.innerHTML.split('<br>');
                    lines.forEach((line, index) => {
                        if (line.trim()) {
                            const detailText = document.createElementNS(svgNS, "text");
                            detailText.setAttribute("x", width / 2);
                            detailText.setAttribute("y", textY + 25 + (index * 16));
                            detailText.setAttribute("text-anchor", "middle");
                            detailText.setAttribute("fill", "white");
                            detailText.setAttribute("font-family", "Arial, sans-serif");
                            detailText.setAttribute("font-size", "12");
                            detailText.setAttribute("opacity", "0.7");
                            detailText.textContent = line.replace(/<[^>]*>/g, ''); // Strip any remaining HTML
                            textGroup.appendChild(detailText);
                        }
                    });
                }

                mainSvg.appendChild(textGroup);
                console.log('✅ Text caption added');
            }

            console.log('🎉 Complete map preview extraction successful!');
            return mainSvg;

        } catch (error) {
            console.error('❌ Error extracting complete map preview:', error);
            throw error;
        }
    }, [extractStarMapSVG]);

    // NEW: Generate PNG from complete SVG preview at high resolution for print quality
    const generateHighResPNG = useCallback(async (resolution = 300, backgroundType = null) => {
        try {
            console.log(`🖼️ Starting complete high-resolution PNG generation at ${resolution} DPI...`);
            console.log('🎯 PNG Generation - backgroundType parameter received:', backgroundType);
            console.log('🎨 This will include: background, orbital decorations, star map, and text');

            // 🚀 MEMORY OPTIMIZATION: Force garbage collection if available
            if (window.gc) {
                console.log('🧹 Forcing garbage collection before PNG generation...');
                window.gc();
            }

            // 🚀 MEMORY OPTIMIZATION: Reduce resolution for development/testing
            const isDevelopment = window.location.hostname === 'localhost';
            const optimizedResolution = isDevelopment && resolution > 150 ? 150 : resolution;

            if (optimizedResolution !== resolution) {
                console.log(`⚡ MEMORY OPTIMIZATION: Reduced resolution from ${resolution} to ${optimizedResolution} DPI for development`);
            }

            // Extract the complete SVG preview (includes all elements) with background type
            const svgElement = await extractCompleteMapPreview(backgroundType);
            if (!svgElement) {
                throw new Error('Failed to extract complete SVG for PNG generation');
            }

            console.log('📊 Complete SVG extracted for PNG generation:', {
                width: svgElement.getAttribute('width'),
                height: svgElement.getAttribute('height'),
                children: svgElement.children.length,
                hasBackground: svgElement.querySelector('rect') ? 'Yes' : 'No',
                hasStarMap: svgElement.querySelector('g') ? 'Yes' : 'No',
                hasText: svgElement.querySelector('text') ? 'Yes' : 'No',
                hasDecorations: svgElement.querySelector('image') ? 'Yes' : 'No',
                imageCount: svgElement.querySelectorAll('image').length,
                imageUrls: Array.from(svgElement.querySelectorAll('image')).map(img => img.getAttribute('href') || img.getAttribute('xlink:href')),
                hasGradientImages: Array.from(svgElement.querySelectorAll('image')).some(img => {
                    const href = img.getAttribute('href') || img.getAttribute('xlink:href');
                    return href && href.includes('product-gradients');
                })
            });

            // Debug: Log the complete SVG structure
            console.log('🔍 Complete SVG structure preview:');
            const svgString = new XMLSerializer().serializeToString(svgElement);
            console.log('SVG content (first 1000 chars):', svgString.substring(0, 1000) + '...');

            // Specifically check for background images
            const backgroundImages = svgElement.querySelectorAll('image');
            console.log('🖼️ Background/decoration images found:', backgroundImages.length);
            backgroundImages.forEach((img, index) => {
                const href = img.getAttribute('href') || img.getAttribute('xlink:href');
                console.log(`Image ${index + 1}:`, {
                    href: href,
                    x: img.getAttribute('x'),
                    y: img.getAttribute('y'),
                    width: img.getAttribute('width'),
                    height: img.getAttribute('height'),
                    isBackgroundGradient: href && href.includes('product-gradients')
                });
            });

            // Log SVG children summary
            console.log('🔍 SVG children summary:');
            Array.from(svgElement.children).forEach((child, index) => {
                console.log(`Child ${index + 1}: ${child.tagName}`, {
                    attributes: Array.from(child.attributes).map(attr => `${attr.name}="${attr.value}"`),
                    isImage: child.tagName === 'image',
                    imageHref: child.tagName === 'image' ? (child.getAttribute('href') || child.getAttribute('xlink:href')) : 'N/A'
                });
            });

            // Get SVG dimensions
            const svgWidth = parseFloat(svgElement.getAttribute('width')) || 600;
            const svgHeight = parseFloat(svgElement.getAttribute('height')) || 750;

            // Calculate high-resolution dimensions with memory-safe limits
            const dpiScale = optimizedResolution / 72;
            let canvasWidth = Math.round(svgWidth * dpiScale);
            let canvasHeight = Math.round(svgHeight * dpiScale);

            // 🚀 MEMORY SAFETY: Limit maximum canvas size to prevent browser crashes
            const MAX_CANVAS_SIZE = 4096; // 4K limit
            const MAX_TOTAL_PIXELS = 16777216; // 16MP limit (4096x4096)

            if (canvasWidth > MAX_CANVAS_SIZE || canvasHeight > MAX_CANVAS_SIZE) {
                const scale = Math.min(MAX_CANVAS_SIZE / canvasWidth, MAX_CANVAS_SIZE / canvasHeight);
                canvasWidth = Math.round(canvasWidth * scale);
                canvasHeight = Math.round(canvasHeight * scale);
                console.log(`⚠️ MEMORY SAFETY: Canvas size limited to ${canvasWidth}x${canvasHeight} (max: ${MAX_CANVAS_SIZE}px)`);
            }

            if (canvasWidth * canvasHeight > MAX_TOTAL_PIXELS) {
                const scale = Math.sqrt(MAX_TOTAL_PIXELS / (canvasWidth * canvasHeight));
                canvasWidth = Math.round(canvasWidth * scale);
                canvasHeight = Math.round(canvasHeight * scale);
                console.log(`⚠️ MEMORY SAFETY: Canvas pixels limited to ${canvasWidth}x${canvasHeight} (max: ${MAX_TOTAL_PIXELS} pixels)`);
            }

            console.log(`📐 Complete PNG dimensions: ${canvasWidth}x${canvasHeight} pixels (${optimizedResolution} DPI)`);
            console.log(`🔍 Scale factor: ${dpiScale.toFixed(2)}x for print quality`);
            console.log(`💾 Estimated memory usage: ${((canvasWidth * canvasHeight * 4) / 1024 / 1024).toFixed(2)} MB`);

            // Create high-resolution canvas with memory monitoring
            const canvas = document.createElement('canvas');
            canvas.width = canvasWidth;
            canvas.height = canvasHeight;
            const ctx = canvas.getContext('2d');

            // Enable maximum quality rendering settings
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.textRenderingOptimization = 'optimizeQuality';

            // Scale the context for high DPI
            ctx.scale(dpiScale, dpiScale);

            // Convert complete SVG to canvas with proper image loading
            return new Promise((resolve, reject) => {
                try {
                    // First, ensure all images in the SVG are loaded by pre-loading them
                    const preloadImages = async (svgElement) => {
                        const imageElements = svgElement.querySelectorAll('image');
                        const imagePromises = [];

                        console.log(`🔄 Pre-loading ${imageElements.length} images from SVG...`);

                        for (const imgElement of imageElements) {
                            const href = imgElement.getAttribute('href') || imgElement.getAttribute('xlink:href');
                            if (href) {
                                const promise = new Promise((resolveImg, rejectImg) => {
                                    const img = new Image();
                                    img.crossOrigin = 'anonymous';
                                    img.onload = () => {
                                        console.log('✅ Pre-loaded image:', href);
                                        resolveImg(img);
                                    };
                                    img.onerror = () => {
                                        console.warn('⚠️ Failed to pre-load image:', href);
                                        resolveImg(null); // Continue even if one image fails
                                    };
                                    img.src = href;
                                });
                                imagePromises.push(promise);
                            }
                        }

                        return Promise.all(imagePromises);
                    };

                    // Pre-load all images first
                    preloadImages(svgElement).then(() => {
                        console.log('✅ All images pre-loaded, starting SVG conversion...');

                        // Serialize the complete SVG with all its content
                        const svgString = new XMLSerializer().serializeToString(svgElement);
                        console.log('📝 Complete SVG serialized, length:', svgString.length, 'characters');

                        // Create blob URL for the complete SVG
                        const svgBlob = new Blob([svgString], {
                            type: 'image/svg+xml;charset=utf-8'
                        });
                        const url = URL.createObjectURL(svgBlob);

                        const img = new Image();
                        img.onload = () => {
                            try {
                                console.log('🎨 Drawing complete preview to high-resolution canvas...');

                                // Clear canvas first
                                ctx.clearRect(0, 0, canvasWidth, canvasHeight);

                                // Draw the complete SVG image to canvas at high resolution
                                ctx.drawImage(img, 0, 0, svgWidth, svgHeight);

                                console.log('✅ Complete preview drawn to canvas successfully');

                                // Add a small delay to ensure everything is properly rendered
                                setTimeout(() => {
                                    // Convert canvas to PNG blob with maximum quality
                                    canvas.toBlob((blob) => {
                                        // 🚀 MEMORY CLEANUP: Immediately clean up resources
                                        URL.revokeObjectURL(url);
                                        canvas.width = 1;
                                        canvas.height = 1;
                                        svgElement.remove();

                                        if (blob) {
                                            console.log(`✅ Complete high-resolution PNG generated successfully!`);
                                            console.log(`📦 Final PNG specs:`, {
                                                size: `${(blob.size / 1024 / 1024).toFixed(2)} MB`,
                                                dimensions: `${canvasWidth}x${canvasHeight}px`,
                                                dpi: `${optimizedResolution} DPI`,
                                                includes: 'Background + Decorations + Star Map + Text'
                                            });

                                            // 🚀 MEMORY CLEANUP: Force garbage collection if available
                                            if (window.gc) {
                                                setTimeout(() => {
                                                    console.log('🧹 Forcing garbage collection after PNG generation...');
                                                    window.gc();
                                                }, 100);
                                            }

                                            resolve(blob);
                                        } else {
                                            reject(new Error('Failed to create PNG blob from canvas'));
                                        }
                                    }, 'image/png', 1.0); // Maximum PNG quality (no compression)
                                }, 100);
                            } catch (drawError) {
                                URL.revokeObjectURL(url);
                                console.error('❌ Error drawing to canvas:', drawError);
                                reject(drawError);
                            }
                        };

                        img.onerror = (error) => {
                            URL.revokeObjectURL(url);
                            console.error('❌ Error loading SVG image:', error);
                            reject(new Error('Failed to load complete SVG image for PNG conversion'));
                        };

                        // Set crossOrigin to handle any potential CORS issues
                        img.crossOrigin = 'anonymous';
                        img.src = url;

                        console.log('⏳ Loading complete SVG image for conversion...');
                    }).catch((preloadError) => {
                        console.error('❌ Error pre-loading images:', preloadError);
                        reject(preloadError);
                    });
                } catch (serializationError) {
                    console.error('❌ Error serializing SVG:', serializationError);
                    reject(serializationError);
                }
            });

        } catch (error) {
            console.error('❌ Error generating complete high-resolution PNG:', error);
            throw error;
        }
    }, [extractCompleteMapPreview]);

    // NEW: Generate PDF using jsPDF and SVG extraction (replaces html2canvas)
    const generatePDFWithJsPDF = useCallback(async (backgroundType = null) => {
        try {
            console.log('📄 Starting jsPDF generation...');

            // 🚀 MEMORY OPTIMIZATION: Force garbage collection before PDF generation
            if (window.gc) {
                console.log('🧹 Forcing garbage collection before PDF generation...');
                window.gc();
            }

            // Extract the complete SVG preview with background type
            const svgElement = await extractCompleteMapPreview(backgroundType);
            if (!svgElement) {
                throw new Error('Failed to extract SVG for PDF generation');
            }

            console.log('📊 SVG extracted for PDF generation');

            // Clean SVG for jsPDF compatibility (fix font issues)
            cleanSVGForJsPDF(svgElement);

            // Get SVG dimensions
            const svgWidth = parseFloat(svgElement.getAttribute('width')) || 600;
            const svgHeight = parseFloat(svgElement.getAttribute('height')) || 750;

            // Calculate PDF dimensions (standard A4 proportions but optimized for star map)
            const pdfWidth = 210; // A4 width in mm
            const pdfHeight = (svgHeight / svgWidth) * pdfWidth; // Maintain aspect ratio

            console.log(`📐 PDF dimensions: ${pdfWidth}mm x ${pdfHeight.toFixed(1)}mm`);

            // Create jsPDF instance with maximum quality settings
            const pdf = new jsPDF({
                orientation: pdfHeight > pdfWidth ? 'portrait' : 'landscape',
                unit: 'mm',
                format: [pdfWidth, pdfHeight],
                compress: false,  // 🚀 DISABLE COMPRESSION for maximum quality
                precision: 16,    // 🚀 MAXIMUM PRECISION for vector elements
                userUnit: 1.0     // 🚀 HIGHEST RESOLUTION scaling
            });

            console.log('📋 PDF document created with MAXIMUM QUALITY settings:', {
                compression: 'DISABLED',
                precision: '16 (maximum)',
                userUnit: '1.0 (highest resolution)',
                dimensions: `${pdfWidth}mm x ${pdfHeight.toFixed(1)}mm`
            });

            // Convert SVG to PDF using svg2pdf.js with maximum quality settings
            await svg2pdf(svgElement, pdf, {
                x: 0,
                y: 0,
                width: pdfWidth,
                height: pdfHeight,
                // 🚀 MAXIMUM QUALITY SETTINGS for svg2pdf.js
                precision: 16,           // Highest precision for coordinates
                fontEmbedding: true,     // Embed fonts for crisp text
                imageQuality: 1.0,       // Maximum image quality (100%)
                vectorizeText: true,     // Keep text as vectors, not rasterized
                preserveAspectRatio: 'none',  // Don't compress aspect ratio
                scale: 1.0              // No downscaling
            });

            console.log('✅ SVG converted to PDF with MAXIMUM QUALITY:', {
                compression: 'DISABLED',
                imageQuality: '100%',
                vectorizedText: 'YES',
                precision: '16-bit coordinates',
                fontEmbedding: 'ENABLED'
            });

            // Generate PDF blob
            const pdfBlob = pdf.output('blob');
            console.log(`📦 PDF blob generated: ${(pdfBlob.size / 1024 / 1024).toFixed(2)} MB`);

            // 🚀 MEMORY CLEANUP: Clean up SVG element
            svgElement.remove();

            // 🚀 MEMORY CLEANUP: Force garbage collection if available
            if (window.gc) {
                setTimeout(() => {
                    console.log('🧹 Forcing garbage collection after PDF generation...');
                    window.gc();
                }, 100);
            }

            return pdfBlob;

        } catch (error) {
            console.error('❌ Error generating PDF with jsPDF:', error);
            throw error;
        }
    }, [extractCompleteMapPreview]);

    // Expose the functions to the parent component
    React.useImperativeHandle(
        ref,
        () => ({
            extractStarMapSVG, // Expose SVG extraction function
            extractCompleteMapPreview, // Expose complete preview extraction function
            generatePDFWithJsPDF, // Expose jsPDF generation function
            generateHighResPNG, // Expose high-resolution PNG generation function
            getSVGString, // Expose SVG string function for debugging
            forceRedraw: () => {
                console.log('Force redraw called');

                // More aggressively create the map container
                let mapElement = document.getElementById('map-container');

                if (!mapElement) {
                    console.log('Creating map container element');
                    mapElement = document.createElement('div');
                    mapElement.id = 'map-container';
                    mapElement.style.width = '100%';
                    mapElement.style.height = '100%';
                    mapElement.style.position = 'absolute';
                    mapElement.style.top = '0';
                    mapElement.style.left = '0';
                    mapElement.style.display = 'block';
                    mapElement.style.borderRadius = '50%';
                    mapElement.style.overflow = 'hidden';
                    mapElement.style.zIndex = '50';

                    // Try multiple places to append the map container
                    let containerAdded = false;

                    // Try the mapContainerRef first
                    if (mapContainerRef.current) {
                        mapContainerRef.current.appendChild(mapElement);
                        containerAdded = true;
                        console.log('Added map container to mapContainerRef');
                    }

                    // If that failed, try finding #celestial-map-container
                    if (!containerAdded) {
                        const celestialContainer = document.getElementById('celestial-map-container');
                        if (celestialContainer) {
                            celestialContainer.appendChild(mapElement);
                            containerAdded = true;
                            console.log('Added map container to #celestial-map-container');
                        }
                    }

                    // If that failed too, try adding to the body as a last resort
                    if (!containerAdded) {
                        const celestialParent = document.querySelector('.circular-map');
                        if (celestialParent) {
                            celestialParent.appendChild(mapElement);
                            containerAdded = true;
                            console.log('Added map container to .circular-map');
                        }
                    }

                    console.log('Map container created and added:', containerAdded);
                } else {
                    console.log('Map container already exists');
                }

                // Try to regenerate the map with multiple attempts
                const tryGenerateMap = (attempt = 1) => {
                    if (isCelestialReady()) {
                        console.log(`Attempt ${attempt}: Celestial is ready, generating map`);
                        try {
                            generateStarMap();
                        } catch (e) {
                            console.error('Error generating star map:', e);
                            // Try again if we haven't reached max attempts
                            if (attempt < 5) {
                                setTimeout(() => tryGenerateMap(attempt + 1), 300);
                            }
                        }
                    } else {
                        console.log(`Attempt ${attempt}: Celestial not ready, waiting`);
                        // Try again if we haven't reached max attempts
                        if (attempt < 5) {
                            setTimeout(() => tryGenerateMap(attempt + 1), 300);
                        }
                    }
                };

                // Start attempting to generate the map
                tryGenerateMap();
            }
        }),
        [extractStarMapSVG, extractCompleteMapPreview, generatePDFWithJsPDF, generateHighResPNG, getSVGString, generateStarMap]
    );

    // Add CSS to hide zoom controls
    useEffect(() => {
        // Add a style to hide zoom controls and fix SVG object positioning
        const style = document.createElement('style');
        style.textContent = `
            #celestial-zoomin, #celestial-zoomout {
                display: none !important;
                visibility: hidden !important;
            }
            
            /* Ensure map container is properly sized */
            #map-container {
                width: 100% !important;
                height: 100% !important;
                position: absolute !important;
                top: 0 !important;
                left: 0 !important;
                border-radius: 6px !important; /* Changed from 50% to 6px for rounded rectangle */
                overflow: hidden !important;
                aspect-ratio: 3/4 !important; /* Changed from 1/1 to 3/4 for portrait orientation */
            }
            
            /* Ensure canvas is properly sized */
            #map-container canvas {
                width: 100% !important;
                height: 100% !important;
                border-radius: 6px !important; /* Changed from 50% to 6px for rounded rectangle */
                position: absolute !important;
                top: 0 !important;
                left: 0 !important;
            }
            
            /* Ensure SVG objects properly fit their containers */
            object[type="image/svg+xml"] {
                width: 100%;
                height: 100%;
                display: block;
            }
            
            /* Ensure proper rendering of orbital decorations */
            .orbital-decoration-wrapper {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                z-index: 100;
                pointer-events: none;
                display: flex;
                align-items: center;
                justify-content: center;
                overflow: visible;
                padding: 24px;
            }
            
            /* Ensure the SVG inside scales properly */
            .orbital-decoration-wrapper object {
                width: 100%;
                height: 100%;
                display: block;
                object-fit: contain;
                max-width: 100%;
                max-height: 100%;
            }

            /* Ensure SVG objects properly fit their containers */
            .orbital-decoration-wrapper img {
                width: 100%;
                height: 100%;
                display: block;
                object-fit: cover;
                max-width: 100%;
                max-height: 100%;
                image-rendering: auto;
                filter: drop-shadow(0 0 2px rgba(255,255,255,0.1));
                
            }
            
            /* Force constellation lines to use the correct subtle style */
            svg path.constellation,
            svg line.constellation,
            svg .constellation-lines path,
            svg .constellation-lines line,
            svg path[class*="constellation"],
            svg line[class*="constellation"] {
                stroke: #8eb4e3 !important;
                stroke-width: 1px !important;
                opacity: 0.3 !important;
                display: inline !important;
                visibility: visible !important;
            }
            
            /* Force stars to use the subtle opacity */
            svg circle.star,
            svg .star,
            svg circle[class*="star"],
            svg .stars circle {
                opacity: 0.3 !important;
            }
            
            /* Force grid and ecliptic lines to always be visible */
            svg path.graticule, 
            svg path.gridline, 
            svg path.equatorial {
                stroke: #8989b3 !important;
                stroke-width: 1px !important;
                opacity: 0.6 !important;
                display: inline !important;
                visibility: visible !important;
            }
            
            svg path.ecliptic {
                stroke: #dda74f !important;
                stroke-width: 1.5px !important;
                opacity: 0.7 !important;
                display: inline !important;
                visibility: visible !important;
            }
        `;
        document.head.appendChild(style);

        // Clean up
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    // Add a resize handler to redraw the map when window is resized
    useEffect(() => {
        const handleResize = () => {
            // If celestial is ready and we have a valid map, redraw it
            if (isCelestialReady() && mapContainerRef.current && mapContainerRef.current.querySelector('canvas')) {
                // Slight delay to allow container to resize first
                setTimeout(() => {
                    if (window.Celestial && window.Celestial.redraw) {
                        console.log('Window resized, redrawing star map');
                        window.Celestial.redraw();
                    }
                }, 200);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);



    return (
        <div
            className="starmap-wrapper"
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                position: 'relative',
                padding: '0',
                overflow: 'visible',
                gap: '30px' /* Use gap for consistent spacing between elements */
            }}
        >
            <StarMapContainer>
                <MapFrame>
                    <MapCanvas ref={mapContainerRef} />
                </MapFrame>

                {/* Show fallback map if there's an error */}
                {error && (
                    <div className="starmap-fallback-container" style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '0',
                        paddingBottom: '100%',
                        zIndex: 1,
                        borderRadius: '50%',
                        overflow: 'hidden'
                    }}>
                        <FallbackStarMap
                            date={date}
                            location={location}
                            caption={caption}
                            style={{ position: 'absolute', width: '100%', height: '100%' }}
                        />
                    </div>
                )}

                {/* Show error message */}
                {error && <ErrorMessage>{error}</ErrorMessage>}
            </StarMapContainer>
        </div>
    );
});

// Export with memo to prevent unnecessary re-renders
export default memo(StarMapGenerator); 